/**
 * CRM operations view — Phase 2.2.c (initial cut: customers + prospects).
 *
 * This file is the eventual home of the entire CRM cluster (customers,
 * prospects, installations, config). The first sub-cut moves the
 * customers + prospects tabs and their shared enquiry-detail expansion
 * helpers; subsequent sub-cuts (2.2.c.2 installations, 2.2.c.3 config +
 * plans) extend `views.crm` with more sub-views.
 *
 * Owns for this slice:
 *   - CRM customers list (`#crmCustomersTbody` + search + pagination).
 *   - CRM prospects list (`#crmProspectsTbody` + search + pagination).
 *   - The "New prospect" form dialog.
 *   - The expandable enquiry-detail strip on each row (lazy-loads
 *     `/api/crm/enquiries` on first expand, caches by id).
 *   - Shared formatters (`formatCrmLabel`, `crmStatusClass`, `truncate`).
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml` (kept generic).
 *   - `openDialog` (the generic dialog opener).
 *   - `formatDateTimeShort` (shared formatter).
 *
 * Boot contract: `admin.html` loads this script after the Phase-1
 * utilities. The IIFE registers `window.adminViews.crm`.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from src/lib.
 */

import { escapeHtml, formatDateTimeShort, formatDateTimeFull, formatCurrency, formatNumber } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { withButtonBusy } from '../../src/lib/button-busy.js';
import { hasRole, getCurrentAdminUser } from '../../src/lib/roles.js';
import { renderCustomerMap } from '../lib/customer-map.js';
import * as quotes from './crm-quotes.js';
import { renderCustomerInvoicing } from './crm-customer-invoicing.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  // ---- Compat aliases (so lifted function bodies need zero changes) ----
  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // `openFromNotification` lives on the tickets list sub-view; reach it lazily
  // so we don't form a hard dependency on tickets.js's parse order. It switches
  // to the Tickets view and selects the ticket.
  const openTicketFromNotification = (id) => {
    const list = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.list;
    if (list && typeof list.openFromNotification === 'function') list.openFromNotification(id);
  };

  // ---- DOM refs (lazy-resolved) -----------------------------------------
  let dom = null;
  function resolveDom() {
    if (dom) return dom;
    dom = {
      // Customers
      crmCustomersView: document.getElementById('crmCustomersView'),
      crmCustomerSearchInput: document.getElementById('crmCustomerSearch'),
      crmCustomersRefreshBtn: document.getElementById('crmCustomersRefresh'),
      crmCustomersTbody: document.getElementById('crmCustomersTbody'),
      crmCustomersPageInfo: document.getElementById('crmCustomersPageInfo'),
      crmCustomersPrevBtn: document.getElementById('crmCustomersPrev'),
      crmCustomersNextBtn: document.getElementById('crmCustomersNext'),
      crmCustomerSegmentFilter: document.getElementById('crmCustomerSegmentFilter'),
      crmCustomerStatusFilter: document.getElementById('crmCustomerStatusFilter'),
      crmCustomerKindFilter: document.getElementById('crmCustomerKindFilter'),
      crmCustomerInactiveFilter: document.getElementById('crmCustomerInactiveFilter'),
      crmCustomerUnlinkedFilter: document.getElementById('crmCustomerUnlinkedFilter'),
      crmCustomersMapToggle: document.getElementById('crmCustomersMapToggle'),
      crmCustomersReportBtn: document.getElementById('crmCustomersReportBtn'),
      crmCustomerReport: document.getElementById('crmCustomerReport'),
      crmCustomerListWrap: document.getElementById('crmCustomerListWrap'),
      crmCustomersPager: document.getElementById('crmCustomersPager'),
      crmCustomerMapWrap: document.getElementById('crmCustomerMapWrap'),
      crmCustomerMapDiv: document.getElementById('crmCustomerMap'),
      crmCustomerMapMeta: document.getElementById('crmCustomerMapMeta'),
      crmCustomerMapExport: document.getElementById('crmCustomerMapExport'),
      // Prospects
      crmProspectsView: document.getElementById('crmProspectsView'),
      crmProspectSearchInput: document.getElementById('crmProspectSearch'),
      crmProspectsRefreshBtn: document.getElementById('crmProspectsRefresh'),
      crmProspectsNewBtn: document.getElementById('crmProspectsNewBtn'),
      crmProspectsReconcileBtn: document.getElementById('crmProspectsReconcileBtn'),
      crmReconcileDialog: document.getElementById('crmReconcileDlg'),
      crmReconcileForm: document.getElementById('crmReconcileForm'),
      crmReconcileSummary: document.getElementById('crmReconcileSummary'),
      crmReconcileList: document.getElementById('crmReconcileList'),
      crmReconcileConfirm: document.getElementById('crmReconcileConfirm'),
      crmProspectTabs: document.getElementById('crmProspectTabs'),
      crmProspectOwnerFilter: document.getElementById('crmProspectOwnerFilter'),
      crmProspectPipelineCard: document.getElementById('crmProspectPipelineCard'),
      crmProspectPipeline: document.getElementById('crmProspectPipeline'),
      crmProspectsTbody: document.getElementById('crmProspectsTbody'),
      crmProspectsPageInfo: document.getElementById('crmProspectsPageInfo'),
      crmProspectsPrevBtn: document.getElementById('crmProspectsPrev'),
      crmProspectsNextBtn: document.getElementById('crmProspectsNext'),
      crmProspectDialog: document.getElementById('crmProspectDlg'),
      crmProspectForm: document.getElementById('crmProspectForm'),
      crmProspectDlgTitle: document.getElementById('crmProspectDlgTitle'),
      crmProspectSubmitBtn: document.getElementById('crmProspectSubmit'),
      crmProspectNameInput: document.getElementById('crmProspectNameInput'),
      crmProspectStatusInput: document.getElementById('crmProspectStatusInput'),
      crmProspectSourceInput: document.getElementById('crmProspectSourceInput'),
      crmProspectPhoneInput: document.getElementById('crmProspectPhoneInput'),
      crmProspectEmailInput: document.getElementById('crmProspectEmailInput'),
      crmProspectCityInput: document.getElementById('crmProspectCityInput'),
      crmProspectAddressInput: document.getElementById('crmProspectAddressInput'),
      crmProspectCompanyInput: document.getElementById('crmProspectCompanyInput'),
      crmProspectTagsInput: document.getElementById('crmProspectTagsInput'),
      crmProspectNotesInput: document.getElementById('crmProspectNotesInput'),
      crmProspectLastContacted: document.getElementById('crmProspectLastContacted'),
      crmProspectOwnerField: document.getElementById('crmProspectOwnerField'),
      crmProspectOwnerInput: document.getElementById('crmProspectOwnerInput'),
      // Customer edit dialog
      crmCustomerDialog: document.getElementById('crmCustomerDlg'),
      crmCustomerForm: document.getElementById('crmCustomerForm'),
      crmCustomerSubmitBtn: document.getElementById('crmCustomerSubmit'),
      crmCustomerNameInput: document.getElementById('crmCustomerNameInput'),
      crmCustomerSegmentInput: document.getElementById('crmCustomerSegmentInput'),
      crmCustomerPhoneInput: document.getElementById('crmCustomerPhoneInput'),
      crmCustomerEmailInput: document.getElementById('crmCustomerEmailInput'),
      crmCustomerAddressInput: document.getElementById('crmCustomerAddressInput'),
      crmCustomerCityInput: document.getElementById('crmCustomerCityInput'),
      crmCustomerRegionInput: document.getElementById('crmCustomerRegionInput'),
      crmCustomerPortaNote: document.getElementById('crmCustomerPortaNote'),
    };
    return dom;
  }

  // Local refs that proxy to dom (so lifted code reads `crmCustomersTbody`
  // etc. unchanged).
  let crmCustomersTbody, crmCustomersPageInfo, crmCustomersPrevBtn, crmCustomersNextBtn,
      crmCustomerSearchInput, crmCustomersRefreshBtn, crmCustomerSegmentFilter, crmCustomerInactiveFilter,
      crmCustomerStatusFilter, crmCustomerKindFilter,
      crmCustomerUnlinkedFilter,
      crmCustomersMapToggle, crmCustomersReportBtn, crmCustomerReport,
      crmCustomerListWrap, crmCustomersPager, crmCustomerMapWrap,
      crmCustomerMapDiv, crmCustomerMapMeta, crmCustomerMapExport,
      crmProspectsTbody, crmProspectsPageInfo, crmProspectsPrevBtn, crmProspectsNextBtn,
      crmProspectSearchInput, crmProspectsRefreshBtn, crmProspectsNewBtn, crmProspectTabs,
      crmProspectsReconcileBtn, crmReconcileDialog, crmReconcileForm, crmReconcileSummary,
      crmReconcileList, crmReconcileConfirm,
      crmProspectOwnerFilter, crmProspectPipelineCard, crmProspectPipeline,
      crmProspectDialog, crmProspectForm, crmProspectDlgTitle, crmProspectSubmitBtn,
      crmProspectNameInput, crmProspectStatusInput, crmProspectSourceInput,
      crmProspectPhoneInput, crmProspectEmailInput,
      crmProspectCityInput, crmProspectAddressInput, crmProspectCompanyInput,
      crmProspectTagsInput, crmProspectNotesInput, crmProspectLastContacted,
      crmProspectOwnerField, crmProspectOwnerInput,
      crmCustomerDialog, crmCustomerForm, crmCustomerSubmitBtn, crmCustomerNameInput,
      crmCustomerSegmentInput, crmCustomerPhoneInput, crmCustomerEmailInput,
      crmCustomerAddressInput, crmCustomerCityInput, crmCustomerRegionInput, crmCustomerPortaNote;
  // The customer currently open in the edit dialog.
  let crmEditingCustomer = null;
  // Matches from the last reconcile preview, pending confirmation.
  let crmReconcileMatches = [];

  // ---- State -----------------------------------------------------------
  const CRM_PAGE_SIZE = 20;
  const crmState = {
    prospects: [],
    prospectsTotal: 0,
    prospectsOffset: 0,
    prospectsSearch: '',
    prospectsStatus: 'active',  // default queue = in-play only (excludes converted/disqualified)
    // Owner filter: '__me__' (my prospects), '' (all owners), or a numeric
    // owner_user_id (supervisors picking a specific rep). Default is decided
    // per-role in mount(): reps land on their own list.
    prospectsOwner: '',
    prospectOwners: [],
    // Worklist follow-up bucket: '' | 'overdue' | 'today' | 'upcoming' | 'none'.
    prospectsDue: '',
    // "Needs attention" filter toggle.
    prospectsAttention: false,
    // Root-only: prospects with a quote awaiting approval.
    prospectsPendingQuotes: false,
    // 'board' (default — Acquisition Board split claim view) or 'table'.
    prospectView: 'board',
    loadingProspects: false,
    customers: [],
    customersTotal: 0,
    customersOffset: 0,
    customersSearch: '',
    customersSegment: '',
    customersStatus: 'active',
    customersKind: '',
    customersInactive: false,
    customersUnlinked: false,
    loadingCustomers: false,
  };
  const crmCustomerDetailCache = new Map();
  const crmProspectDetailCache = new Map();
  let crmCustomerSearchTimer = null;
  let crmProspectSearchTimer = null;
  // When set, the prospect dialog is in edit mode for this prospect id.
  let crmEditingProspectId = null;
  // The prospect object currently open in the dialog (for log-contact/timeline).
  let crmEditingProspect = null;

  // ---- Prospect ownership helpers --------------------------------------
  const _PROSPECT_SUPERVISOR_ROLES = ['root', 'sales_supervisor', 'customer_care_supervisor'];

  function currentUserId() {
    const me = getCurrentAdminUser();
    const id = me && me.id != null ? Number(me.id) : null;
    return Number.isFinite(id) ? id : null;
  }

  function isProspectSupervisor() {
    return hasRole(..._PROSPECT_SUPERVISOR_ROLES);
  }

  // Resolve the active owner filter to a concrete owner_user_id, or null = all.
  function resolveOwnerFilterId() {
    const value = crmState.prospectsOwner;
    if (value === '__me__') return currentUserId();
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  // Append one <option> per owner to a <select>, after its static options.
  // Re-runnable: removes previously-added dynamic options first and restores
  // the current selection.
  function fillOwnerOptions(select, { skipSelf = false } = {}) {
    if (!select) return;
    const current = select.value;
    Array.from(select.querySelectorAll('option[data-dynamic="1"]')).forEach((o) => o.remove());
    const myId = currentUserId();
    (crmState.prospectOwners || []).forEach((owner) => {
      if (skipSelf && owner.owner_user_id === myId) return;
      const opt = document.createElement('option');
      opt.value = String(owner.owner_user_id);
      opt.textContent = owner.owner_name;
      opt.dataset.dynamic = '1';
      select.appendChild(opt);
    });
    select.value = current;
  }

  async function loadProspectOwners() {
    try {
      const data = await adminAPI.fetch(API.crmProspectOwners(), { method: 'GET' });
      crmState.prospectOwners = Array.isArray(data && data.owners) ? data.owners : [];
      // "My prospects" already covers self in the filter, so skip it there.
      fillOwnerOptions(crmProspectOwnerFilter, { skipSelf: true });
      fillOwnerOptions(crmProspectOwnerInput);
    } catch (err) {
      // Non-fatal: the Mine/All filter still works without the rep list.
      // eslint-disable-next-line no-console
      console.warn('crm: failed to load prospect owners', err);
    }
  }

  async function assignProspectToMe(prospect) {
    const myId = currentUserId();
    if (!prospect || myId == null) return;
    try {
      await adminAPI.fetch(API.crmProspect(prospect.id), {
        method: 'PATCH',
        body: JSON.stringify({ owner_user_id: myId }),
      });
      showFlash('Prospect assigned to you');
      loadCrmProspects();
    } catch (err) {
      showFlash(err?.message || 'Failed to assign prospect', 'error');
    }
  }

  // ---- Click-to-act + follow-up helpers --------------------------------
  function _waLink(phone) {
    const digits = String(phone || '').replace(/[^0-9]/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  }

  // Render a contact cell with tappable tel: / WhatsApp / mailto links.
  function buildContactLinks(cell, item) {
    cell.innerHTML = '';
    let any = false;
    if (item.phone_number) {
      any = true;
      const line = document.createElement('div');
      const tel = document.createElement('a');
      tel.href = `tel:${item.phone_number}`;
      tel.textContent = item.phone_number;
      tel.title = 'Call';
      line.appendChild(tel);
      const wa = _waLink(item.phone_number);
      if (wa) {
        const waLink = document.createElement('a');
        waLink.href = wa;
        waLink.target = '_blank';
        waLink.rel = 'noopener';
        waLink.textContent = '💬';
        waLink.title = 'WhatsApp';
        waLink.style.cssText = 'margin-left:6px;text-decoration:none;';
        line.appendChild(waLink);
      }
      cell.appendChild(line);
    }
    if (item.email) {
      any = true;
      const mail = document.createElement('a');
      mail.href = `mailto:${item.email}`;
      mail.textContent = item.email;
      mail.title = 'Email';
      mail.style.fontSize = '12px';
      cell.appendChild(mail);
    }
    if (!any) cell.textContent = '—';
  }

  // Relative follow-up label + colour (overdue=red, today=orange, future=blue).
  function formatFollowUp(item) {
    const raw = item.next_follow_up_at;
    if (!raw) return { text: '—', color: 'var(--sub)', title: 'No follow-up set' };
    const ts = Date.parse(raw);
    if (Number.isNaN(ts)) return { text: '—', color: 'var(--sub)', title: '' };
    const dayMs = 86400000;
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const sameDay = ts >= startToday.getTime() && ts < startToday.getTime() + dayMs;
    const diffMs = ts - Date.now();
    const absDays = Math.max(1, Math.round(Math.abs(diffMs) / dayMs));
    let text;
    let color;
    if (sameDay) {
      text = 'Today';
      color = '#E65100';
    } else if (diffMs < 0) {
      text = `${absDays}d overdue`;
      color = '#C62828';
    } else {
      text = absDays <= 1 ? 'Tomorrow' : `in ${absDays}d`;
      color = '#1565C0';
    }
    return { text, color, title: formatDateTimeFull(ts) };
  }

  // Bucket a prospect into a worklist section based on its next follow-up.
  // Overdue → Today → Upcoming → No follow-up. Used to render grouped
  // section headers in the prospects table so reps work the list top-down.
  function followUpBucket(item) {
    const raw = item && item.next_follow_up_at;
    const ts = raw ? Date.parse(raw) : NaN;
    if (Number.isNaN(ts)) {
      return { key: 'none', label: 'No follow-up scheduled', rank: 3, ts: Infinity };
    }
    const dayMs = 86400000;
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const startMs = startToday.getTime();
    if (ts < startMs) return { key: 'overdue', label: 'Overdue', rank: 0, ts };
    if (ts < startMs + dayMs) return { key: 'today', label: 'Due today', rank: 1, ts };
    return { key: 'upcoming', label: 'Upcoming', rank: 2, ts };
  }

  const BUCKET_STYLES = {
    overdue: { color: '#C62828', bg: '#fdecea' },
    today: { color: '#E65100', bg: '#fff4e5' },
    upcoming: { color: '#1565C0', bg: '#e8f1fb' },
    none: { color: 'var(--sub)', bg: 'var(--panel-2,#f4f5f7)' },
  };

  // ---- Log contact modal -----------------------------------------------
  let crmLogContactTarget = null;

  function openLogContactDialog(prospect, { afterSave } = {}) {
    const dlg = document.getElementById('crmLogContactDlg');
    if (!dlg || !prospect) return;
    crmLogContactTarget = { prospect, afterSave: afterSave || null };
    const who = document.getElementById('crmLogContactWho');
    if (who) who.textContent = prospect.full_name || 'Prospect';
    const form = document.getElementById('crmLogContactForm');
    if (form) form.reset();
    const ch = document.getElementById('crmLogContactChannel');
    if (ch) ch.value = 'call';
    openDialog(dlg);
  }

  async function submitLogContact() {
    if (!crmLogContactTarget) return;
    const { prospect, afterSave } = crmLogContactTarget;
    const val = (id) => {
      const el = document.getElementById(id);
      return el ? el.value : '';
    };
    const payload = { activity_type: 'contact', channel: val('crmLogContactChannel') || 'call' };
    const outcome = val('crmLogContactOutcome');
    const body = (val('crmLogContactBody') || '').trim();
    const status = val('crmLogContactStatus');
    const followLocal = val('crmLogContactFollowUp');
    if (outcome) payload.outcome = outcome;
    if (body) payload.body = body;
    if (status) payload.status = status;
    if (followLocal) {
      const dt = new Date(followLocal);
      if (!Number.isNaN(dt.getTime())) payload.next_follow_up_at = dt.toISOString();
    }
    const submitBtn = document.getElementById('crmLogContactSubmit');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving…'; }
    try {
      await adminAPI.fetch(API.crmProspectActivities(prospect.id), { method: 'POST', body: JSON.stringify(payload) });
      showFlash('Contact logged');
      const dlg = document.getElementById('crmLogContactDlg');
      if (dlg) { try { dlg.close(); } catch { dlg.open = false; } }
      if (typeof afterSave === 'function') afterSave();
    } catch (err) {
      showFlash(err?.message || 'Failed to log contact', 'error');
    } finally {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Save contact'; }
    }
  }

  // ---- Confirm for installation ----------------------------------------
  // Sales marks a worked prospect ready; billing links + creates the
  // installation later (when the customer pays).
  async function confirmProspect(prospect) {
    if (!prospect) return;
    try {
      await adminAPI.fetch(API.crmProspectConfirm(prospect.id), { method: 'POST' });
      showFlash(`${prospect.full_name} confirmed — ready for installation`);
      closeCrmProspectDialog();
      refreshProspectsCurrentView();
    } catch (err) {
      showFlash(err?.message || 'Failed to confirm prospect', 'error');
    }
  }

  // ---- Convert + duplicate guard ---------------------------------------
  async function convertProspect(prospect) {
    if (!prospect) return;
    let existing = null;
    try {
      const params = new URLSearchParams();
      if (prospect.phone_number) params.set('phone', prospect.phone_number);
      if (prospect.email) params.set('email', prospect.email);
      params.set('exclude_id', prospect.id);
      const dup = await adminAPI.fetch(API.crmProspectCheckDuplicate(params.toString()), { method: 'GET' });
      const matches = Array.isArray(dup && dup.customer_matches) ? dup.customer_matches : [];
      if (matches.length) existing = matches[0];
    } catch (err) {
      // Non-fatal: fall back to creating a new customer.
    }

    let body = {};
    if (existing) {
      const link = confirm(`Found an existing customer "${existing.name}".\n\nOK = link this prospect to them.\nCancel = create a NEW customer instead.`);
      body = link ? { support_customer_id: existing.id } : {};
    } else if (!confirm(`Convert "${prospect.full_name}" into a customer?`)) {
      return;
    }

    try {
      const res = await adminAPI.fetch(API.crmProspectConvert(prospect.id), { method: 'POST', body: JSON.stringify(body) });
      showFlash(res && res.created_customer ? 'Converted — new customer created' : 'Converted — linked to customer');
      closeCrmProspectDialog();
      refreshProspectsCurrentView();
    } catch (err) {
      showFlash(err?.message || 'Failed to convert prospect', 'error');
    }
  }

  let crmDupCheckTimer = null;
  function scheduleDuplicateCheck() {
    // Only warn while creating a new prospect (not when editing an existing one).
    if (crmEditingProspectId) return;
    if (crmDupCheckTimer) clearTimeout(crmDupCheckTimer);
    crmDupCheckTimer = setTimeout(runDuplicateCheck, 400);
  }

  async function runDuplicateCheck() {
    const warn = document.getElementById('crmProspectDupWarning');
    if (!warn) return;
    const phone = (crmProspectPhoneInput && crmProspectPhoneInput.value.trim()) || '';
    const email = (crmProspectEmailInput && crmProspectEmailInput.value.trim()) || '';
    if (phone.length < 6 && !email) { warn.style.display = 'none'; warn.textContent = ''; return; }
    try {
      const params = new URLSearchParams();
      if (phone) params.set('phone', phone);
      if (email) params.set('email', email);
      const dup = await adminAPI.fetch(API.crmProspectCheckDuplicate(params.toString()), { method: 'GET' });
      const parts = [];
      (dup.prospect_matches || []).forEach((m) => parts.push(`already a prospect — ${m.name}${m.detail ? ` (${m.detail})` : ''}`));
      (dup.customer_matches || []).forEach((m) => parts.push(`already a customer — ${m.name}`));
      if (parts.length) {
        warn.textContent = `⚠ ${parts.join(' · ')}`;
        warn.style.display = '';
      } else {
        warn.style.display = 'none';
        warn.textContent = '';
      }
    } catch (err) {
      // Non-fatal: a soft warning; ignore failures.
    }
  }

  // ---- Activity timeline (prospect dialog) -----------------------------
  function _activityLine(a) {
    const t = a.activity_type;
    const meta = a.metadata || {};
    if (t === 'contact') {
      const ch = a.channel ? formatCrmLabel(a.channel) : 'Contact';
      return a.outcome ? `${ch} — ${formatCrmLabel(a.outcome)}` : ch;
    }
    if (t === 'created') return 'Prospect created';
    if (t === 'status_change') return `Status: ${formatCrmLabel(meta.from || '')} → ${formatCrmLabel(meta.to || '')}`;
    if (t === 'assignment') return 'Reassigned';
    if (t === 'follow_up_set') {
      return meta.next_follow_up_at
        ? `Follow-up set: ${formatDateTimeFull(meta.next_follow_up_at)}`
        : 'Follow-up cleared';
    }
    if (t === 'note') return 'Note';
    if (t === 'conversion') return 'Converted';
    return formatCrmLabel(t);
  }

  function renderProspectTimeline(list, items) {
    list.innerHTML = '';
    if (!items.length) {
      list.innerHTML = '<div style="color:var(--sub);">No activity yet.</div>';
      return;
    }
    items.forEach((a) => {
      const row = document.createElement('div');
      row.style.cssText = 'padding:6px 0; border-bottom:1px solid var(--border,#f0f0f0);';
      const head = document.createElement('div');
      head.style.cssText = 'display:flex; justify-content:space-between; gap:8px;';
      const left = document.createElement('span');
      left.style.fontWeight = '600';
      left.textContent = _activityLine(a);
      const right = document.createElement('span');
      right.style.cssText = 'color:var(--sub); font-size:11px; white-space:nowrap;';
      right.textContent = a.created_at ? formatDateTimeShort(a.created_at) : '';
      head.appendChild(left);
      head.appendChild(right);
      row.appendChild(head);
      const bits = [];
      if (a.actor_name) bits.push(a.actor_name);
      if (a.body) bits.push(a.body);
      if (bits.length) {
        const sub = document.createElement('div');
        sub.style.cssText = 'color:var(--sub); margin-top:2px;';
        sub.textContent = bits.join(' · ');
        row.appendChild(sub);
      }
      list.appendChild(row);
    });
  }

  async function loadProspectTimeline(prospectId) {
    const wrap = document.getElementById('crmProspectTimelineWrap');
    const list = document.getElementById('crmProspectTimeline');
    if (!wrap || !list) return;
    wrap.style.display = '';
    list.innerHTML = '<div style="color:var(--sub);">Loading…</div>';
    try {
      const data = await adminAPI.fetch(API.crmProspectActivities(prospectId), { method: 'GET' });
      renderProspectTimeline(list, Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      list.innerHTML = '<div style="color:var(--err);">Failed to load activity</div>';
    }
  }

  // ---- Acquisition Board ------------------------------------------------
  let crmBoardPoolItems = [];
  // Hot-deals pool pagination — when the first page is mostly leads already
  // being worked, reps can page deeper into the pool.
  const BOARD_POOL_PAGE = 20;
  let crmBoardPoolOffset = 0;
  let crmBoardPoolTotal = 0;

  // "My queue" is a working view: one column per in-play stage. Converted /
  // disqualified are deliberately absent (the API scopes the queue to in-play),
  // so a closed lead can never occupy a column here.
  const QUEUE_COLUMNS = [
    { key: 'new', bodyId: 'crmQueueNew', countId: 'crmQueueCountNew' },
    { key: 'contacted', bodyId: 'crmQueueContacted', countId: 'crmQueueCountContacted' },
    { key: 'qualified', bodyId: 'crmQueueQualified', countId: 'crmQueueCountQualified' },
  ];

  function isBoardActive() {
    const pv = document.getElementById('crmProspectsView');
    if (pv && pv.style.display === 'none') return false; // navigated away
    const bv = document.getElementById('crmProspectBoardView');
    return !!(bv && bv.style.display !== 'none');
  }

  function _boardCard(item, { claimable }) {
    const card = document.createElement('div');
    card.style.cssText = 'border:1px solid var(--border,#e5e7eb); border-radius:8px; padding:8px 10px; background:var(--bg,#fff);';
    card.dataset.prospectId = item.id;
    if (claimable) card.draggable = true;

    const top = document.createElement('div');
    top.style.cssText = 'display:flex; justify-content:space-between; gap:8px; align-items:center;';
    const name = document.createElement('button');
    name.type = 'button';
    name.textContent = item.full_name || '(unnamed)';
    name.style.cssText = 'background:none;border:none;padding:0;font:inherit;font-weight:600;color:var(--link,#2563eb);cursor:pointer;text-align:left;';
    name.addEventListener('click', () => openCrmProspectDialog(item));
    top.appendChild(name);
    const meter = prospectMeter(item);
    if (meter) {
      const badge = document.createElement('span');
      badge.textContent = meter.label;
      badge.style.cssText = `padding:0 6px;border-radius:9px;font-size:10px;font-weight:700;color:${meter.color};background:${meter.bg};`;
      top.appendChild(badge);
    }
    card.appendChild(top);

    const tag = document.createElement('div');
    if (claimable) {
      tag.style.cssText = 'font-size:11px;color:var(--sub);margin-top:2px;';
      tag.textContent = item.owner_user_id == null ? 'New · unassigned' : `From care · ${item.owner_name || ''}`;
    } else {
      const fu = formatFollowUp(item);
      tag.style.cssText = `font-size:11px;margin-top:2px;color:${fu.color};`;
      tag.textContent = fu.text === '—' ? 'No follow-up set' : `Follow-up: ${fu.text}`;
      tag.title = fu.title;
    }
    card.appendChild(tag);

    // My-queue cards: show last engagement and flag stale leads (no contact in
    // 7+ days) light red so they stand out for follow-up.
    if (!claimable) {
      const dayMs = 86400000;
      const last = item.last_contacted_at ? Date.parse(item.last_contacted_at) : null;
      const stale = last == null || (Date.now() - last) > 7 * dayMs;
      const eng = document.createElement('div');
      eng.style.cssText = 'font-size:11px;margin-top:2px;color:var(--sub);';
      if (last == null) {
        eng.textContent = 'Last engaged: never';
      } else {
        const days = Math.floor((Date.now() - last) / dayMs);
        eng.textContent = `Last engaged: ${formatDateTimeShort(item.last_contacted_at)} (${days}d ago)`;
      }
      card.appendChild(eng);
      if (stale) {
        card.style.background = '#fff5f5';
        card.style.borderColor = '#fecaca';
      }
    }

    const contact = document.createElement('div');
    contact.style.cssText = 'margin-top:4px;font-size:12px;';
    buildContactLinks(contact, item);
    card.appendChild(contact);

    const actions = document.createElement('div');
    actions.style.cssText = 'margin-top:6px; display:flex; gap:6px;';
    if (claimable) {
      const claimBtn = document.createElement('button');
      claimBtn.type = 'button';
      claimBtn.className = 'btn ghost small';
      claimBtn.textContent = 'Claim';
      claimBtn.addEventListener('click', () => claimFromBoard(item));
      actions.appendChild(claimBtn);
    } else {
      const logBtn = document.createElement('button');
      logBtn.type = 'button';
      logBtn.className = 'btn ghost small';
      logBtn.textContent = '📞 Log';
      logBtn.addEventListener('click', () => openLogContactDialog(item, { afterSave: loadBoard }));
      actions.appendChild(logBtn);

      // The board has no row drawer, so quotes open in a standalone modal.
      const quoteBtn = document.createElement('button');
      quoteBtn.type = 'button';
      quoteBtn.className = 'btn ghost small';
      quoteBtn.textContent = '📄 Quotes';
      quoteBtn.addEventListener('click', () => quotes.openQuotesDialog(item));
      actions.appendChild(quoteBtn);
    }
    card.appendChild(actions);

    if (claimable) {
      card.addEventListener('dragstart', (e) => {
        if (e.dataTransfer) {
          e.dataTransfer.setData('text/plain', String(item.id));
          e.dataTransfer.effectAllowed = 'move';
        }
        card.style.opacity = '0.5';
      });
      card.addEventListener('dragend', () => { card.style.opacity = ''; });
    }
    return card;
  }

  async function claimFromBoard(item) {
    const myId = currentUserId();
    if (myId == null) { showFlash('Could not identify you', 'error'); return; }
    try {
      await adminAPI.fetch(API.crmProspect(item.id), { method: 'PATCH', body: JSON.stringify({ owner_user_id: myId }) });
      showFlash(`Claimed ${item.full_name || 'lead'}`);
      await loadBoard();
      // Claiming starts the work: prompt to log first contact + set a follow-up.
      openLogContactDialog(item, { afterSave: loadBoard });
    } catch (err) {
      showFlash(err?.message || 'Could not claim — it may already be taken', 'error');
      await loadBoard();
    }
  }

  async function loadBoard() {
    const poolEl = document.getElementById('crmBoardPool');
    const queueEl = document.getElementById('crmBoardQueue');
    const poolCount = document.getElementById('crmBoardPoolCount');
    const queueCount = document.getElementById('crmBoardQueueCount');
    if (!poolEl || !queueEl) return;
    const myId = currentUserId();
    try {
      const [poolData, queueData] = await Promise.all([
        adminAPI.fetch(API.crmProspectPool(`limit=${BOARD_POOL_PAGE}&offset=${crmBoardPoolOffset}`), { method: 'GET' }),
        myId != null
          ? adminAPI.fetch(API.crmProspects(`owner_user_id=${myId}&limit=50`), { method: 'GET' })
          : Promise.resolve({ items: [] }),
      ]);
      const pool = Array.isArray(poolData?.items) ? poolData.items : [];
      crmBoardPoolItems = pool;
      crmBoardPoolTotal = Number(poolData?.total || 0);
      // If the page we landed on is now empty (leads claimed since), step back
      // to the last page that still has cards.
      if (!pool.length && crmBoardPoolOffset > 0 && crmBoardPoolTotal > 0) {
        const lastOffset = Math.max(0, (Math.ceil(crmBoardPoolTotal / BOARD_POOL_PAGE) - 1) * BOARD_POOL_PAGE);
        if (lastOffset !== crmBoardPoolOffset) {
          crmBoardPoolOffset = lastOffset;
          return loadBoard();
        }
      }
      let queue = Array.isArray(queueData?.items) ? queueData.items : [];
      // Worklist order: soonest follow-up first, undated last.
      queue = queue.slice().sort((a, b) => {
        const ta = a.next_follow_up_at ? Date.parse(a.next_follow_up_at) : Infinity;
        const tb = b.next_follow_up_at ? Date.parse(b.next_follow_up_at) : Infinity;
        return ta - tb;
      });
      poolEl.innerHTML = '';
      if (!pool.length) {
        poolEl.innerHTML = '<div class="hint" style="color:var(--sub);font-size:12px;">No hot deals right now.</div>';
      } else {
        pool.forEach((it) => poolEl.appendChild(_boardCard(it, { claimable: true })));
      }
      // Split the worklist across the three in-play stage columns. Closed leads
      // (converted/disqualified) can't appear: the API scopes the queue to
      // CRMProspectStatus.in_play(), and this is a working view.
      const emptyEl = document.getElementById('crmBoardQueueEmpty');
      if (emptyEl) emptyEl.style.display = queue.length ? 'none' : '';
      QUEUE_COLUMNS.forEach((col) => {
        const body = document.getElementById(col.bodyId);
        const countEl = document.getElementById(col.countId);
        if (!body) return;
        const rows = queue.filter((q) => String(q.status || '').toLowerCase() === col.key);
        body.innerHTML = '';
        if (!rows.length) {
          body.innerHTML = '<div class="hint" style="color:var(--sub);font-size:11px;">—</div>';
        } else {
          rows.forEach((it) => body.appendChild(_boardCard(it, { claimable: false })));
        }
        if (countEl) countEl.textContent = String(rows.length);
      });
      updateAttentionChip();
      renderBoardPoolPager();
      if (poolCount) poolCount.textContent = String(crmBoardPoolTotal);
      if (queueCount) {
        const overdue = queue.filter((q) => {
          const ts = q.next_follow_up_at ? Date.parse(q.next_follow_up_at) : NaN;
          return !Number.isNaN(ts) && ts < Date.now();
        }).length;
        queueCount.textContent = overdue ? `${queue.length} · ${overdue} overdue` : String(queue.length);
        queueCount.style.color = overdue ? '#C62828' : 'var(--sub)';
      }
    } catch (err) {
      poolEl.innerHTML = '<div style="color:var(--err);font-size:12px;">Failed to load board</div>';
    }
  }

  function renderBoardPoolPager() {
    const pager = document.getElementById('crmBoardPoolPager');
    const info = document.getElementById('crmBoardPoolPageInfo');
    const prev = document.getElementById('crmBoardPoolPrev');
    const next = document.getElementById('crmBoardPoolNext');
    if (!pager) return;
    const multiPage = crmBoardPoolTotal > BOARD_POOL_PAGE;
    pager.style.display = multiPage ? 'flex' : 'none';
    if (!multiPage) return;
    const start = crmBoardPoolTotal ? crmBoardPoolOffset + 1 : 0;
    const end = Math.min(crmBoardPoolOffset + BOARD_POOL_PAGE, crmBoardPoolTotal);
    if (info) info.textContent = `${start}–${end} of ${crmBoardPoolTotal}`;
    if (prev) prev.disabled = crmBoardPoolOffset <= 0;
    if (next) next.disabled = end >= crmBoardPoolTotal;
  }

  function setupBoardDrop() {
    const queueEl = document.getElementById('crmBoardQueue');
    const pane = document.getElementById('crmBoardQueuePane');
    if (!queueEl || queueEl.dataset.dropWired) return;
    queueEl.dataset.dropWired = '1';
    const onOver = (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      if (pane) pane.style.outline = '2px dashed var(--link,#2563eb)';
    };
    const onLeave = () => { if (pane) pane.style.outline = ''; };
    const onDrop = (e) => {
      e.preventDefault();
      if (pane) pane.style.outline = '';
      const id = e.dataTransfer ? e.dataTransfer.getData('text/plain') : '';
      if (!id) return;
      const item = crmBoardPoolItems.find((p) => String(p.id) === String(id));
      if (item) claimFromBoard(item);
    };
    [queueEl, pane].forEach((el) => {
      if (!el) return;
      el.addEventListener('dragover', onOver);
      el.addEventListener('dragleave', onLeave);
      el.addEventListener('drop', onDrop);
    });
  }

  // Live refresh — the board/worklist now updates via WebSocket pushes
  // (`crm.prospect.changed`) instead of a 25s poll. `onProspectChanged` is
  // wired into the admin notification dispatcher; we debounce so a burst of
  // events (e.g. a bulk reassignment) only triggers one reload.
  let crmRefreshDebounce = null;
  function onProspectChanged() {
    const pv = document.getElementById('crmProspectsView');
    if (pv && pv.style.display === 'none') return; // not on the prospects view
    if (crmRefreshDebounce) clearTimeout(crmRefreshDebounce);
    crmRefreshDebounce = setTimeout(() => {
      crmRefreshDebounce = null;
      if (isBoardActive()) loadBoard();
      else loadCrmProspects();
      updateAttentionChip();
    }, 600);
  }

  function setProspectView(mode) {
    const tableView = document.getElementById('crmProspectTableView');
    const boardView = document.getElementById('crmProspectBoardView');
    const tabs = document.getElementById('crmProspectTabs');
    const dueFilter = document.getElementById('crmProspectDueFilter');
    const pipeline = document.getElementById('crmProspectPipelineCard');
    const toggle = document.getElementById('crmProspectViewToggle');
    const board = mode === 'board';
    crmState.prospectView = board ? 'board' : 'table';
    if (tableView) tableView.style.display = board ? 'none' : '';
    if (boardView) boardView.style.display = board ? '' : 'none';
    if (tabs) tabs.style.display = board ? 'none' : '';
    if (dueFilter) dueFilter.style.display = board ? 'none' : '';
    if (pipeline) pipeline.style.display = board ? 'none' : '';
    if (toggle) toggle.textContent = board ? '☰ Table' : '🎯 Board';
    if (board) {
      setupBoardDrop();
      crmBoardPoolOffset = 0; // always open on page 1 of the pool
      loadBoard();
    } else {
      loadCrmProspects();
    }
  }

  // Phase 3.2.c: helpers come from src/lib/* directly (imports at top).

  // ---- Enquiry-detail expansion helpers --------------------------------
  function createCrmDetailMarkup(containerId) {
    return `
      <div class="crm-detail" id="${containerId}" data-loaded="0" data-loading="0">
        <div class="crm-detail-header">
          <strong>Enquiries</strong>
          <span class="crm-detail-sub">Latest 10 records</span>
        </div>
        <div class="crm-detail-body" data-role="enquiries-body">
          <div class="crm-detail-empty">Expand to load enquiries.</div>
        </div>
        <div class="crm-quotes-section" data-role="quotes" style="margin-top:16px;"></div>
      </div>`;
  }

  // Customer expand: billing + open tickets (loaded from the customer detail
  // endpoint) above the standard enquiries strip. The enquiries header/body keep
  // the same structure so renderCrmEnquiriesInto works unchanged.
  function createCrmCustomerDetailMarkup(containerId) {
    return `
      <div class="crm-detail" id="${containerId}" data-loaded="0" data-loading="0" data-extras="0" data-extras-loading="0">
        <div data-role="toolbar"></div>
        <div class="crm-customer-grid" style="display:grid; grid-template-columns:repeat(auto-fit, minmax(260px, 1fr)); gap:16px; align-items:start;">
          <div data-role="billing"><div class="crm-detail-empty">Expand to load billing.</div></div>
          <div data-role="tickets"><div class="crm-detail-empty">Expand to load tickets.</div></div>
          <div>
            <div class="crm-detail-header">
              <strong>Enquiries</strong>
              <span class="crm-detail-sub">Latest 10 records</span>
            </div>
            <div class="crm-detail-body" data-role="enquiries-body">
              <div class="crm-detail-empty">Expand to load enquiries.</div>
            </div>
          </div>
        </div>
        <div class="crm-customer-invoicing-grid" style="display:grid; grid-template-columns:repeat(auto-fit, minmax(320px, 1fr)); gap:16px; align-items:start; margin-top:16px;">
          <div class="crm-customer-invoicing" data-role="invoicing">
            <div class="crm-detail-empty">Expand to load invoicing.</div>
          </div>
          <div class="crm-quotes-section" data-role="customer-quotes">
            <div class="crm-detail-empty">Expand to load quotations.</div>
          </div>
        </div>
        <div class="crm-customer-activity" data-role="activity" style="margin-top:16px;">
          <div class="crm-detail-empty">Expand to load activity.</div>
        </div>
      </div>`;
  }

  const _fmtMoney = (n) => (n == null ? '—' : formatCurrency(n));

  function renderBillingBlock(el, item, billing) {
    if (!el) return;
    el.innerHTML = '';
    const header = document.createElement('div');
    header.className = 'crm-detail-header';
    const title = document.createElement('strong');
    title.textContent = 'Billing';
    header.appendChild(title);
    const refreshBtn = document.createElement('button');
    refreshBtn.type = 'button';
    refreshBtn.className = 'btn';
    refreshBtn.dataset.role = 'refresh-billing';
    refreshBtn.textContent = '↻ Refresh';
    refreshBtn.style.cssText = 'margin-left:auto; font-size:11px; padding:2px 8px;';
    header.appendChild(refreshBtn);
    el.appendChild(header);

    const body = document.createElement('div');
    body.className = 'crm-detail-body';
    if (!billing || !billing.has_account) {
      body.innerHTML = '<div class="crm-detail-empty">No billing account linked.</div>';
      refreshBtn.disabled = true;
      // Offer a quick link to a PortaBilling profile for edit-capable roles.
      if (hasRole(..._CUSTOMER_EDIT_ROLES)) {
        const linkBtn = document.createElement('button');
        linkBtn.type = 'button';
        linkBtn.className = 'btn primary';
        linkBtn.textContent = '🔗 Link to PortaBilling';
        linkBtn.style.cssText = 'margin-left:8px; font-size:11px; padding:2px 8px;';
        linkBtn.addEventListener('click', () => openPortaLinkPicker(item, {
          afterLink: (billingSummary) => renderBillingBlock(el, item, billingSummary),
        }));
        header.appendChild(linkBtn);
      }
      el.appendChild(body);
      return;
    }
    refreshBtn.addEventListener('click', () => refreshCustomerBilling(item, el));
    const lines = [`<div><span class="crm-detail-sub">Balance due:</span> <strong>${_fmtMoney(billing.balance_due)}</strong></div>`];
    if (billing.outstanding_balance != null) lines.push(`<div><span class="crm-detail-sub">Outstanding:</span> ${_fmtMoney(billing.outstanding_balance)}</div>`);
    if (billing.overdue_balance != null) lines.push(`<div><span class="crm-detail-sub">Overdue:</span> ${_fmtMoney(billing.overdue_balance)}</div>`);
    const lp = billing.last_payment;
    if (lp && (lp.amount != null || lp.at)) {
      const when = lp.at ? escapeHtml(formatDateTimeShort(lp.at)) : '';
      lines.push(`<div><span class="crm-detail-sub">Last payment:</span> ${_fmtMoney(lp.amount)}${when ? ' · ' + when : ''}</div>`);
    } else {
      lines.push('<div><span class="crm-detail-sub">Last payment:</span> —</div>');
    }
    if (billing.as_of) lines.push(`<div class="crm-detail-sub">As of ${escapeHtml(formatDateTimeShort(billing.as_of))}</div>`);
    body.innerHTML = lines.join('');
    el.appendChild(body);
  }

  function renderTicketsBlock(el, tickets, count) {
    if (!el) return;
    el.innerHTML = '';
    const header = document.createElement('div');
    header.className = 'crm-detail-header';
    const title = document.createElement('strong');
    title.textContent = `Open tickets${count ? ` (${count})` : ''}`;
    header.appendChild(title);
    el.appendChild(header);

    const body = document.createElement('div');
    body.className = 'crm-detail-body';
    if (!Array.isArray(tickets) || !tickets.length) {
      body.innerHTML = '<div class="crm-detail-empty">No open tickets.</div>';
      el.appendChild(body);
      return;
    }
    const list = document.createElement('ul');
    list.className = 'crm-enquiry-list crm-scroll-cap';
    const orderedTickets = tickets.slice().sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    orderedTickets.forEach((t) => {
      const li = document.createElement('li');
      li.className = 'crm-enquiry-item';
      const head = document.createElement('div');
      head.className = 'crm-enquiry-item-head';
      if (t.status) {
        const s = document.createElement('span');
        s.className = `crm-enquiry-status ${crmStatusClass(t.status)}`;
        s.textContent = formatCrmLabel(t.status);
        head.appendChild(s);
      }
      if (t.priority) {
        const p = document.createElement('span');
        p.className = 'crm-enquiry-channel';
        p.textContent = formatCrmLabel(t.priority);
        head.appendChild(p);
      }
      if (t.created_at) {
        const d = document.createElement('span');
        d.className = 'crm-enquiry-date';
        d.textContent = formatDateTimeShort(t.created_at);
        head.appendChild(d);
      }
      li.appendChild(head);
      const link = document.createElement('button');
      link.type = 'button';
      link.title = 'Open ticket';
      link.style.cssText = 'background:none; border:none; padding:0; font:inherit; color:var(--link,#2563eb); text-align:left; text-decoration:underline; cursor:pointer;';
      const code = t.short_code ? `#${t.short_code} · ` : '';
      link.textContent = `${code}${t.subject || 'Ticket'}`;
      link.addEventListener('click', (event) => {
        event.stopPropagation();
        openTicketFromNotification(t.id);
      });
      li.appendChild(link);
      list.appendChild(li);
    });
    body.appendChild(list);
    el.appendChild(body);
  }

  // Call-center agents/supervisors (and sales supervisors, root) can edit
  // customer identity + Porta sync. Mirror the backend `_REQUIRE_EDIT_ROLES`
  // so the Details button only shows to roles the API will accept (no 403).
  const _CUSTOMER_EDIT_ROLES = ['root', 'customer_care', 'customer_care_supervisor', 'sales_supervisor'];

  // A real PortaBilling link is a numeric i_customer; null/empty or a
  // "manual-…"/"crm-…" placeholder means the customer is effectively unlinked.
  function _isRealPorta(id) {
    return /^\d+$/.test(String(id == null ? '' : id).trim());
  }

  const _KIND_LABELS = { customer: 'Customer', prospect: 'Prospect', survey: 'Survey', non_customer: 'Not a customer' };
  const _KINDS = ['customer', 'prospect', 'survey', 'non_customer'];

  // The record kind is chosen from a fixed list (not typed) — free-text was
  // error-prone. The select + reason live in #crmReclassifyDlg (admin.html).
  let _reclassifyTarget = null;

  function reclassifyCustomerRecord(item) {
    const dlg = document.getElementById('crmReclassifyDlg');
    if (!dlg) return;
    _reclassifyTarget = item;
    const title = document.getElementById('crmReclassifyTitle');
    if (title) title.textContent = `Reclassify "${item.display_name || 'this record'}"`;
    const sel = document.getElementById('crmReclassifyKind');
    if (sel) {
      sel.innerHTML = _KINDS
        .map((k) => `<option value="${escapeHtml(k)}">${escapeHtml(_KIND_LABELS[k] || k)}</option>`)
        .join('');
      sel.value = _KINDS.includes(item.kind) ? item.kind : 'customer';
    }
    const reason = document.getElementById('crmReclassifyReason');
    if (reason) reason.value = '';
    openDialog(dlg);
  }

  async function saveReclassify() {
    const item = _reclassifyTarget;
    if (!item) return;
    const sel = document.getElementById('crmReclassifyKind');
    const reasonEl = document.getElementById('crmReclassifyReason');
    const kind = sel ? sel.value : '';
    if (!_KINDS.includes(kind)) { showFlash('Select a record kind.', 'error'); return; }
    const reason = (reasonEl ? reasonEl.value : '').trim();
    if (!reason) { showFlash('A reason is required.', 'error'); return; }
    const btn = document.getElementById('crmReclassifySave');
    if (btn) btn.disabled = true;
    try {
      await adminAPI.fetch(API.crmCustomerReclassify(item.id),
        { method: 'POST', body: JSON.stringify({ kind, reason }) });
      showFlash(kind === 'prospect' ? 'Reclassified — prospect created if eligible.' : 'Record reclassified.');
      const dlg = document.getElementById('crmReclassifyDlg');
      if (dlg) closeDialog(dlg);
      loadCrmCustomers();
    } catch (err) {
      showFlash(adminAPI.formatErrorDetail(err) || 'Reclassify failed', 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function closeCustomerRecord(item) {
    const reopening = (item.status || 'active') === 'archived';
    const verb = reopening ? 'Reopen' : 'Close';
    const reasonRaw = window.prompt(`${verb} "${item.display_name || 'this record'}".\n\nReason (required):`, '');
    if (reasonRaw === null) return;
    const reason = reasonRaw.trim();
    if (!reason) { showFlash('A reason is required.', 'error'); return; }
    try {
      await adminAPI.fetch(API.crmCustomerClose(item.id),
        { method: 'POST', body: JSON.stringify({ status: reopening ? 'active' : 'archived', reason }) });
      showFlash(reopening ? 'Record reopened.' : 'Record closed (archived).');
      loadCrmCustomers();
    } catch (err) {
      showFlash(adminAPI.formatErrorDetail(err) || 'Action failed', 'error');
    }
  }

  function renderCustomerExtras(container, item, detail) {
    const toolbar = container.querySelector('[data-role="toolbar"]');
    if (toolbar) {
      toolbar.innerHTML = '';
      if (hasRole(..._CUSTOMER_EDIT_ROLES)) {
        toolbar.style.cssText = 'display:flex; align-items:center; justify-content:flex-end; gap:6px; margin-bottom:8px;';

        // Classification/status badge — only when not a plain active customer.
        const kind = item.kind || 'customer';
        const st = item.status || 'active';
        if (kind !== 'customer' || st !== 'active') {
          const badge = document.createElement('span');
          badge.style.cssText = 'margin-right:auto; font-size:11px; padding:2px 8px; border-radius:8px; background:#fef3c7; color:#92400e;';
          badge.textContent = `${_KIND_LABELS[kind] || kind}${st !== 'active' ? ` · ${st}` : ''}`;
          toolbar.appendChild(badge);
        }

        const editBtn = document.createElement('button');
        editBtn.type = 'button';
        editBtn.className = 'btn';
        editBtn.style.cssText = 'font-size:11px; padding:2px 8px;';
        editBtn.textContent = '✎ Edit details';
        editBtn.title = 'Edit customer information';
        editBtn.addEventListener('click', () => openCrmCustomerDialog(item));
        toolbar.appendChild(editBtn);

        // Reclassifying and closing/archiving a record are supervisor actions —
        // restricted to the customer-care supervisor and root (enforced server-side too).
        if (hasRole('customer_care_supervisor', 'root')) {
          const reclassBtn = document.createElement('button');
          reclassBtn.type = 'button';
          reclassBtn.className = 'btn';
          reclassBtn.style.cssText = 'font-size:11px; padding:2px 8px;';
          reclassBtn.textContent = '⇄ Reclassify';
          reclassBtn.title = 'Change record kind (customer / prospect / survey / non-customer)';
          reclassBtn.addEventListener('click', () => reclassifyCustomerRecord(item));
          toolbar.appendChild(reclassBtn);

          const closeBtn = document.createElement('button');
          closeBtn.type = 'button';
          closeBtn.className = 'btn';
          closeBtn.style.cssText = 'font-size:11px; padding:2px 8px;';
          closeBtn.textContent = st === 'archived' ? '↩ Reopen' : '🗑 Close';
          closeBtn.title = st === 'archived' ? 'Reopen this record' : 'Close (archive) this record';
          closeBtn.addEventListener('click', () => closeCustomerRecord(item));
          toolbar.appendChild(closeBtn);
        }
      }
    }
    renderBillingBlock(container.querySelector('[data-role="billing"]'), item, detail.billing);
    renderTicketsBlock(container.querySelector('[data-role="tickets"]'), detail.open_tickets, detail.open_ticket_count);
    renderCustomerInvoicing(container.querySelector('[data-role="invoicing"]'), item, detail);
    renderCustomerQuotes(container.querySelector('[data-role="customer-quotes"]'), item);
    renderCustomerActivitySection(container.querySelector('[data-role="activity"]'), item);
  }

  // ---- Customer 360 unified timeline (phase 2) ---------------------------

  const CRM_ACTIVITY_PAGE = 20;
  const _TIMELINE_ICON = {
    note: '📝', contact: '📞', system: '⚙️', enquiry: '❓', ticket: '🎫',
    visit: '📍', installation: '🛠️', payment: '💰', billing: '🧾', prospect: '🌱',
  };
  const _TIMELINE_FILTERS = [
    { label: 'All', kinds: null },
    { label: 'Notes & contacts', kinds: ['note', 'contact', 'system'] },
    { label: 'Tickets', kinds: ['ticket', 'visit'] },
    { label: 'Enquiries', kinds: ['enquiry'] },
    { label: 'Installation', kinds: ['installation', 'payment'] },
    { label: 'Billing', kinds: ['billing'] },
    { label: 'Prospect', kinds: ['prospect'] },
  ];

  function _activityChangesText(meta) {
    const changes = meta && meta.changes;
    if (!changes || typeof changes !== 'object') return '';
    const parts = Object.keys(changes).map((field) => {
      const c = changes[field] || {};
      const from = c.from == null || c.from === '' ? '∅' : String(c.from);
      const to = c.to == null || c.to === '' ? '∅' : String(c.to);
      return `${field}: ${from} → ${to}`;
    });
    let txt = parts.join(' · ');
    if (meta.porta_sync) txt += `  ·  PortaOne: ${meta.porta_sync}`;
    return txt;
  }

  function _gpsLink(gps) {
    if (!gps || gps.lat == null || gps.lng == null) return '';
    const q = `${gps.lat},${gps.lng}`;
    return `<a class="crm-activity-gps" href="https://www.google.com/maps?q=${encodeURIComponent(q)}" target="_blank" rel="noopener">📍 ${escapeHtml(q)}</a>`;
  }

  function renderTimelineEvent(e) {
    const kind = String(e.kind || '').toLowerCase();
    const icon = _TIMELINE_ICON[kind] || '•';
    const when = e.at ? escapeHtml(formatDateTimeShort(e.at)) : '';
    const who = e.actor ? escapeHtml(e.actor) : '';
    const meta = e.metadata || {};
    const tags = [];
    if (meta.channel) tags.push(escapeHtml(formatCrmLabel(meta.channel)));
    if (meta.outcome) tags.push(escapeHtml(formatCrmLabel(meta.outcome)));
    if (meta.payment_type) tags.push(escapeHtml(formatCrmLabel(meta.payment_type)));
    const tagHtml = tags.length ? `<span class="crm-activity-tags">${tags.join(' · ')}</span>` : '';
    let bodyText = e.body ? escapeHtml(e.body) : '';
    if (kind === 'system') {
      const diff = _activityChangesText(meta);
      if (diff) bodyText += `<div class="crm-activity-diff">${escapeHtml(diff)}</div>`;
    }
    const gps = _gpsLink(e.gps);
    const metaLine = who ? `${when} · ${who}` : when;
    const pin = e.pinned ? '<span class="crm-activity-pin" title="Pinned">📌</span> ' : '';

    // Edit/pin/delete controls for the user's own manual note/contact (or any, if supervisor).
    const editable = (kind === 'note' || kind === 'contact') && String(e.id || '').startsWith('act:')
      && (Number(e.actor_user_id) === currentUserId()
          || hasRole('root', 'customer_care_supervisor', 'sales_supervisor'));
    let actions = '';
    if (editable) {
      const aid = escapeHtml(String(e.id).slice(4));
      actions = `<span class="crm-activity-actions">
        <button type="button" class="crm-link" data-act-action="pin" data-act-id="${aid}" data-pinned="${e.pinned ? '1' : '0'}">${e.pinned ? 'Unpin' : 'Pin'}</button>
        <button type="button" class="crm-link" data-act-action="edit" data-act-id="${aid}">Edit</button>
        <button type="button" class="crm-link" data-act-action="delete" data-act-id="${aid}">Delete</button>
      </span>`;
    }

    return `
      <li class="crm-activity-item crm-tl-${kind}${e.pinned ? ' is-pinned' : ''}">
        <span class="crm-activity-icon">${icon}</span>
        <div class="crm-activity-main">
          <div class="crm-activity-head">
            <strong>${pin}${escapeHtml(e.title || formatCrmLabel(kind) || 'Event')}</strong>
            ${tagHtml}
            <span class="crm-activity-when">${metaLine}</span>
          </div>
          ${bodyText ? `<div class="crm-activity-body">${bodyText}</div>` : ''}
          ${gps ? `<div class="crm-activity-body">${gps}</div>` : ''}
          ${actions}
        </div>
      </li>`;
  }

  // Quotations for an existing customer — reuses the prospect quote flow via a
  // linked (or newly created, hidden) prospect. Find-only on view so browsing a
  // customer never creates a row; the holder is created on demand at "New quote".
  const _QUOTE_VIEW_ROLES = ['root', 'sales_officer', 'sales_supervisor', 'customer_care_supervisor'];
  const _QUOTE_EDIT_ROLES = ['root', 'sales_officer', 'sales_supervisor'];

  async function renderCustomerQuotes(el, item) {
    if (!el || !item || !item.id) return;
    if (!hasRole(..._QUOTE_VIEW_ROLES)) { el.innerHTML = ''; return; }
    el.innerHTML = '<div class="crm-detail-header"><strong>Quotations</strong></div><div class="crm-detail-empty">Loading quotations…</div>';
    try {
      const res = await adminAPI.fetch(API.crmCustomerQuoteProspect(item.id), { method: 'GET' });
      if (res && res.id) {
        // The customer already has a quote holder — mount the full section (list + New).
        await quotes.mountQuotesSection(el, res);
        return;
      }
      // No quotes yet: offer to start one (creating the holder only then).
      const canEdit = hasRole(..._QUOTE_EDIT_ROLES);
      el.innerHTML = `<div class="crm-detail-header"><strong>Quotations</strong></div>
        <div class="crm-detail-body"><div class="crm-detail-empty">No quotations yet — for a service upgrade or a new service.</div>
        ${canEdit ? '<button class="btn" data-cust-new-quote style="margin-top:8px;">＋ New quote</button>' : ''}</div>`;
      const newBtn = el.querySelector('[data-cust-new-quote]');
      if (newBtn) {
        newBtn.addEventListener('click', async () => {
          newBtn.disabled = true;
          try {
            const prospect = await adminAPI.fetch(API.crmCustomerQuoteProspect(item.id), { method: 'POST' });
            await quotes.mountQuotesSection(el, prospect);
            const sectionNew = el.querySelector('[data-q-new]');
            if (sectionNew) sectionNew.click();   // open the builder straight away
          } catch (err) {
            showFlash((err && err.message) ? err.message : 'Could not start a quote.', 'error');
            newBtn.disabled = false;
          }
        });
      }
    } catch (err) {
      el.innerHTML = `<div class="crm-detail-header"><strong>Quotations</strong></div><div class="crm-detail-error">${escapeHtml(err && err.message ? err.message : 'Failed to load quotations.')}</div>`;
    }
  }

  function renderCustomerActivitySection(el, item) {
    if (!el) return;
    const state = { filterIdx: 0, offset: 0 };
    const chipHtml = _TIMELINE_FILTERS.map((f, i) =>
      `<button type="button" class="btn crm-tl-chip${i === 0 ? ' active' : ''}" data-tl-filter="${i}" style="font-size:11px; padding:2px 8px;">${escapeHtml(f.label)}</button>`
    ).join(' ');
    el.innerHTML = `
      <div class="crm-detail-header">
        <strong>Customer timeline</strong>
        <button type="button" class="btn" data-role="activity-add" style="margin-left:auto; font-size:11px; padding:2px 8px;">＋ Log contact / note</button>
      </div>
      <div class="crm-followup" style="display:flex; align-items:center; gap:8px; margin:6px 0; flex-wrap:wrap; font-size:12px;">
        <span class="crm-detail-sub">Next follow-up:</span>
        <strong data-role="fu-display">none</strong>
        <input type="date" data-role="fu-date" style="font-size:12px;" />
        <button type="button" class="btn" data-role="fu-set" style="font-size:11px; padding:2px 8px;">Set</button>
        <button type="button" class="btn" data-role="fu-clear" style="font-size:11px; padding:2px 8px; display:none;">Clear</button>
      </div>
      <form data-role="activity-form" class="crm-activity-form" style="display:none; gap:8px; margin:8px 0; flex-wrap:wrap; align-items:flex-end;">
        <label class="field" style="margin:0;"><span>Type</span>
          <select data-role="act-type"><option value="contact">Contact</option><option value="note">Note</option></select>
        </label>
        <label class="field" data-role="act-channel-wrap" style="margin:0;"><span>Channel</span>
          <select data-role="act-channel">
            <option value="">—</option><option value="call">Call</option><option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option><option value="sms">SMS</option><option value="visit">Visit</option><option value="other">Other</option>
          </select>
        </label>
        <label class="field" data-role="act-outcome-wrap" style="margin:0;"><span>Outcome</span>
          <select data-role="act-outcome">
            <option value="">—</option><option value="reached">Reached</option><option value="no_answer">No answer</option>
            <option value="callback_requested">Callback requested</option><option value="interested">Interested</option>
            <option value="not_interested">Not interested</option><option value="wrong_number">Wrong number</option>
          </select>
        </label>
        <label class="field" style="margin:0; flex:1 1 220px;"><span>Note</span>
          <input type="text" data-role="act-body" maxlength="4000" placeholder="What happened / note…" />
        </label>
        <button type="submit" class="btn primary" style="font-size:12px;">Save</button>
      </form>
      <div class="crm-tl-chips" data-role="tl-chips" style="display:flex; flex-wrap:wrap; gap:6px; margin:8px 0;">${chipHtml}</div>
      <ul class="crm-activity-list crm-scroll-cap" data-role="activity-list" style="list-style:none; margin:0; padding:0;"></ul>
      <div data-role="activity-more" style="margin-top:8px;"></div>`;

    const addBtn = el.querySelector('[data-role="activity-add"]');
    const form = el.querySelector('[data-role="activity-form"]');
    const typeSel = el.querySelector('[data-role="act-type"]');
    const chWrap = el.querySelector('[data-role="act-channel-wrap"]');
    const outWrap = el.querySelector('[data-role="act-outcome-wrap"]');

    const syncTypeFields = () => {
      const isContact = typeSel.value === 'contact';
      chWrap.style.display = isContact ? '' : 'none';
      outWrap.style.display = isContact ? '' : 'none';
    };
    typeSel.addEventListener('change', syncTypeFields);
    syncTypeFields();

    addBtn.addEventListener('click', () => {
      const showing = form.style.display !== 'none';
      form.style.display = showing ? 'none' : 'flex';
      if (!showing) { const b = el.querySelector('[data-role="act-body"]'); if (b) b.focus(); }
    });

    el.querySelector('[data-role="tl-chips"]').addEventListener('click', (ev) => {
      const btn = ev.target.closest('[data-tl-filter]');
      if (!btn) return;
      state.filterIdx = Number(btn.dataset.tlFilter) || 0;
      el.querySelectorAll('[data-tl-filter]').forEach((b) => b.classList.toggle('active', b === btn));
      loadCustomerTimeline(item, el, state, false);
    });

    // Follow-up set/clear.
    const fuDate = el.querySelector('[data-role="fu-date"]');
    const fuDisplay = el.querySelector('[data-role="fu-display"]');
    const fuClearBtn = el.querySelector('[data-role="fu-clear"]');
    const renderFollowUp = () => {
      const v = item.next_follow_up_at;
      fuDisplay.textContent = v ? formatDateTimeShort(v) : 'none';
      fuClearBtn.style.display = v ? '' : 'none';
      if (v) { try { fuDate.value = new Date(v).toISOString().slice(0, 10); } catch {} }
    };
    renderFollowUp();
    const postFollowUp = async (iso) => {
      try {
        const updated = await adminAPI.fetch(API.crmCustomerFollowUp(item.id), {
          method: 'POST', body: JSON.stringify({ next_follow_up_at: iso }),
        });
        item.next_follow_up_at = updated.next_follow_up_at;
        renderFollowUp();
        showFlash(iso ? 'Follow-up set' : 'Follow-up cleared');
        loadCustomerTimeline(item, el, state, false);
      } catch (err) {
        showFlash((err && err.message) || 'Failed to update follow-up', 'error');
      }
    };
    el.querySelector('[data-role="fu-set"]').addEventListener('click', () => {
      if (!fuDate.value) { showFlash('Pick a follow-up date.', 'error'); return; }
      postFollowUp(new Date(fuDate.value).toISOString());
    });
    fuClearBtn.addEventListener('click', () => postFollowUp(null));

    // Pin / edit / delete on manual note/contact entries (event-delegated).
    el.querySelector('[data-role="activity-list"]').addEventListener('click', async (ev) => {
      const btn = ev.target.closest('[data-act-action]');
      if (!btn) return;
      const aid = btn.dataset.actId;
      const action = btn.dataset.actAction;
      try {
        if (action === 'pin') {
          await adminAPI.fetch(API.crmCustomerActivity(item.id, aid), {
            method: 'PATCH', body: JSON.stringify({ pinned: btn.dataset.pinned !== '1' }),
          });
        } else if (action === 'edit') {
          const li = btn.closest('.crm-activity-item');
          const cur = li ? (li.querySelector('.crm-activity-body')?.textContent || '') : '';
          const next = window.prompt('Edit note text:', cur);
          if (next === null) return;
          await adminAPI.fetch(API.crmCustomerActivity(item.id, aid), {
            method: 'PATCH', body: JSON.stringify({ body: next }),
          });
        } else if (action === 'delete') {
          if (!window.confirm('Delete this entry?')) return;
          await adminAPI.fetch(API.crmCustomerActivity(item.id, aid), { method: 'DELETE' });
        }
        loadCustomerTimeline(item, el, state, false);
      } catch (err) {
        showFlash((err && err.message) || 'Action failed', 'error');
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const payload = {
        activity_type: typeSel.value,
        channel: el.querySelector('[data-role="act-channel"]').value || null,
        outcome: el.querySelector('[data-role="act-outcome"]').value || null,
        body: (el.querySelector('[data-role="act-body"]').value || '').trim() || null,
      };
      if (payload.activity_type === 'contact' && !payload.channel && !payload.body) {
        showFlash('Add a channel or a note for the contact.', 'error');
        return;
      }
      try {
        await adminAPI.fetch(API.crmCustomerActivities(item.id), {
          method: 'POST', body: JSON.stringify(payload),
        });
        form.reset(); syncTypeFields(); form.style.display = 'none';
        showFlash('Activity logged');
        // Server invalidated the cache on write; reload the merged feed.
        loadCustomerTimeline(item, el, state, false);
      } catch (err) {
        showFlash((err && err.message) || 'Failed to log activity', 'error');
      }
    });

    loadCustomerTimeline(item, el, state, false);
  }

  async function loadCustomerTimeline(item, sectionEl, state, append) {
    const listEl = sectionEl.querySelector('[data-role="activity-list"]');
    const moreEl = sectionEl.querySelector('[data-role="activity-more"]');
    if (!listEl) return;
    if (!append) { state.offset = 0; listEl.innerHTML = '<li class="crm-detail-loading">Loading timeline…</li>'; }
    try {
      const params = new URLSearchParams({ limit: String(CRM_ACTIVITY_PAGE), offset: String(state.offset) });
      const filter = _TIMELINE_FILTERS[state.filterIdx];
      if (filter && filter.kinds) filter.kinds.forEach((k) => params.append('kinds', k));
      const data = await adminAPI.fetch(API.crmCustomerTimeline(item.id, params.toString()), { method: 'GET' });
      const items = Array.isArray(data && data.items) ? data.items : [];
      if (!append) listEl.innerHTML = '';
      if (!items.length && !append) {
        listEl.innerHTML = '<li class="crm-detail-empty">No timeline events for this filter.</li>';
      } else {
        listEl.insertAdjacentHTML('beforeend', items.map(renderTimelineEvent).join(''));
      }
      state.offset += items.length;
      moreEl.innerHTML = '';
      if (data && data.has_more) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn';
        btn.style.cssText = 'font-size:11px; padding:2px 8px;';
        btn.textContent = 'Load older';
        btn.addEventListener('click', () => { btn.disabled = true; loadCustomerTimeline(item, sectionEl, state, true); });
        moreEl.appendChild(btn);
      }
    } catch (err) {
      if (!append) listEl.innerHTML = '<li class="crm-detail-error">Failed to load timeline.</li>';
    }
  }

  // ---- Customer map view (Customer 360 phase 4) --------------------------
  // Rendering lives in js/lib/customer-map.js (shared with Tech Support).

  function toggleCrmCustomerMap() {
    const showMap = crmCustomerMapWrap && crmCustomerMapWrap.style.display === 'none';
    if (crmCustomerMapWrap) crmCustomerMapWrap.style.display = showMap ? '' : 'none';
    if (crmCustomerListWrap) crmCustomerListWrap.style.display = showMap ? 'none' : '';
    if (crmCustomersPager) crmCustomersPager.style.display = showMap ? 'none' : '';
    if (crmCustomersMapToggle) crmCustomersMapToggle.textContent = showMap ? '☰ List' : '🗺 Map';
    if (showMap) loadCrmCustomerMap();
  }

  // ---- Strategic insights report (customer base health) -------------------
  let _customerReportData = null;
  let _churnDim = 'region';   // 'region' | 'product'
  const _CHURN_SEGMENTS = [
    ['active', 'Active', '#10b981'],
    ['suspended', 'Suspended', '#f59e0b'],
    ['blocked', 'Blocked', '#ef4444'],
  ];

  function _kpi(label, value, color, segment) {
    // A segment makes the tile a clickable drill-down (view + export those customers).
    const clickable = segment
      ? ` data-segment="${escapeHtml(segment)}" role="button" tabindex="0" title="Click to view & export these customers" style="min-width:96px; cursor:pointer;"`
      : ' style="min-width:96px;"';
    return `<div${clickable}>
      <div class="muted" style="font-size:11px;">${escapeHtml(label)}${segment ? ' 🔎' : ''}</div>
      <strong style="font-size:18px;${color ? ` color:${color};` : ''}">${value}</strong>
    </div>`;
  }

  // Stacked horizontal bars: one row per region/product, segmented active vs
  // suspended vs blocked. Bar length is scaled to the biggest group (volume) and
  // the trailing label shows the churn (suspended+blocked) share.
  function _stackedChurn(data) {
    const rows = Object.entries(data || {}).map(([k, v]) => {
      const active = v.active || 0, suspended = v.suspended || 0, blocked = v.blocked || 0;
      const total = active + suspended + blocked;
      return { k, active, suspended, blocked, total, churn: suspended + blocked };
    }).filter((r) => r.total > 0)
      .sort((a, b) => b.churn - a.churn || b.total - a.total)
      .slice(0, 12);
    if (!rows.length) return '<div class="muted" style="font-size:12px;">No data.</div>';
    const maxTotal = Math.max(...rows.map((r) => r.total));
    const seg = (n, color, label) => (n > 0
      ? `<span title="${label}: ${n}" style="display:block; height:14px; width:${(n / maxTotal) * 100}%; background:${color};"></span>`
      : '');
    const bars = rows.map((r) => {
      const churnPct = Math.round((r.churn / r.total) * 100);
      return `<div style="display:flex; align-items:center; gap:8px; font-size:12px; margin:3px 0;">
        <span style="flex:0 0 140px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;" title="${escapeHtml(r.k)}">${escapeHtml(r.k)}</span>
        <span style="flex:1; display:flex; background:var(--panel-2,#f1f5f9); border-radius:6px; overflow:hidden;">
          ${seg(r.active, '#10b981', 'Active')}${seg(r.suspended, '#f59e0b', 'Suspended')}${seg(r.blocked, '#ef4444', 'Blocked')}
        </span>
        <span style="flex:0 0 96px; text-align:right;" title="Churned (suspended + blocked) of total">${r.churn}/${r.total} · ${churnPct}%</span>
      </div>`;
    }).join('');
    const legend = '<div style="display:flex; gap:12px; flex-wrap:wrap; margin-top:6px; font-size:12px;">'
      + _CHURN_SEGMENTS.map(([, label, color]) => `<span><span style="display:inline-block; width:10px; height:10px; border-radius:2px; background:${color}; margin-right:4px;"></span>${label}</span>`).join('')
      + '</div>';
    return bars + legend;
  }

  function renderCustomerReport(r) {
    if (!crmCustomerReport) return;
    _customerReportData = r;
    const fmt = (n) => formatNumber(n);
    const dimData = _churnDim === 'product' ? r.by_product : r.by_region;
    const tabBtn = (dim, label) => `<button type="button" class="btn ghost" data-churn-dim="${dim}" style="font-size:11px; padding:2px 8px;${_churnDim === dim ? ' font-weight:700; text-decoration:underline;' : ''}">${label}</button>`;
    crmCustomerReport.innerHTML = `
      <div class="card" style="padding:12px;">
        <div style="display:flex; gap:18px; flex-wrap:wrap; margin-bottom:12px;">
          ${_kpi('Active', fmt(r.active), '#10b981', 'active')}
          ${_kpi('Suspended', fmt(r.suspended), '#f59e0b', 'suspended')}
          ${_kpi('Blocked', fmt(r.blocked), '#ef4444', 'blocked')}
          ${_kpi('Active classes', fmt(r.active_classes))}
          ${_kpi('Susp. <1 month', fmt(r.suspended_under_1m), '#ca8a04', 'suspended_under_1m')}
          ${_kpi('Susp. 1–2 months', fmt(r.suspended_1_2m), '#d97706', 'suspended_1_2m')}
          ${_kpi('Susp. 2–3 months', fmt(r.suspended_2_3m), '#b45309', 'suspended_2_3m')}
          ${_kpi('Susp. 3–6 months', fmt(r.suspended_3_6m), '#9a3412', 'suspended_3_6m')}
          ${_kpi('Susp. >6 months', fmt(r.suspended_over_6m), '#991b1b', 'suspended_over_6m')}
          ${_kpi('No GPS', fmt(r.no_gps), '#6b7280', 'no_gps')}
          ${_kpi('Unlinked (to reconcile)', fmt(r.unlinked), '#8b5cf6', 'unlinked')}
          ${_kpi('No billing data', fmt(r.no_billing), '#6b7280', 'no_billing')}
          ${_kpi('Total', fmt(r.total), null, 'total')}
        </div>
        <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:6px;">
          <span class="muted" style="font-size:12px;">Churn by</span>
          ${tabBtn('region', 'Region')}${tabBtn('product', 'Product')}
          <span class="muted" style="font-size:11px;">— active vs suspended vs blocked</span>
        </div>
        ${_stackedChurn(dimData)}
        <div id="crmCustomerSegmentDrill" style="margin-top:12px;"></div>
      </div>`;
  }

  async function loadCustomerReport() {
    if (!crmCustomerReport) return;
    crmCustomerReport.innerHTML = '<div class="muted">Loading report…</div>';
    try {
      const r = await adminAPI.fetch(API.crmCustomerReport(), { method: 'GET', cache: 'no-store' });
      renderCustomerReport(r);
    } catch (err) {
      crmCustomerReport.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err) || 'Failed to load report')}</div>`;
    }
  }

  function _customerReportOpen() {
    return !!(crmCustomerReport && crmCustomerReport.style.display !== 'none');
  }

  // Keep an open report in sync when the customer list is refreshed.
  function refreshCustomerReportIfOpen() {
    if (_customerReportOpen()) loadCustomerReport();
  }

  async function toggleCustomerReport() {
    if (!crmCustomerReport) return;
    if (_customerReportOpen()) { crmCustomerReport.style.display = 'none'; return; }
    crmCustomerReport.style.display = '';
    loadCustomerReport();
  }

  // ---- Report drill-down: the customers behind a KPI tile + CSV export ----
  const SEGMENT_LABELS = {
    active: 'Active customers', suspended: 'Suspended', blocked: 'Blocked',
    suspended_under_1m: 'Suspended <1 month', suspended_1_2m: 'Suspended 1–2 months',
    suspended_2_3m: 'Suspended 2–3 months', suspended_3_6m: 'Suspended 3–6 months',
    suspended_over_6m: 'Suspended >6 months',
    long_suspended_3m: 'Suspended >3 months', long_suspended_6m: 'Suspended >6 months',
    no_gps: 'Customers with no GPS',
    unlinked: 'Unlinked customers (no Porta link — reconcile)',
    no_billing: 'Linked but no billing snapshot (unsynced)',
    total: 'All customers',
  };

  async function openSegmentDrill(segment) {
    const box = document.getElementById('crmCustomerSegmentDrill');
    if (!box) return;
    box.innerHTML = '<div class="muted" style="font-size:12px;">Loading…</div>';
    try {
      const data = await adminAPI.fetch(API.crmCustomerReportSegment(segment), { method: 'GET', cache: 'no-store' });
      renderSegmentDrill(segment, data);
    } catch (err) {
      box.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err) || 'Failed to load')}</div>`;
    }
  }

  function renderSegmentDrill(segment, data) {
    const box = document.getElementById('crmCustomerSegmentDrill');
    if (!box) return;
    const items = (data && data.items) || [];
    const esc = (v) => escapeHtml(v == null ? '' : String(v));
    const money = (v) => (v == null ? '—' : formatCurrency(v));
    const rows = items.slice(0, 500).map((c) => `<tr>
      <td>${esc(c.display_name)}</td><td>${esc(c.primary_phone)}</td><td>${esc(c.region)}</td>
      <td>${esc(c.account_state)}</td><td>${esc(c.customer_class)}</td><td>${esc(c.plan)}</td>
      <td style="text-align:right;">${money(c.overdue_balance)}</td>
      <td style="text-align:right;">${c.days_overdue == null ? '—' : c.days_overdue}</td>
      <td style="text-align:right;">${c.suspension_days == null ? '—' : c.suspension_days}</td>
      <td style="text-align:center;">${c.has_gps ? '✓' : '—'}</td></tr>`).join('');
    box.innerHTML = `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:6px; flex-wrap:wrap; border-top:1px solid var(--border,#eee); padding-top:10px;">
        <strong style="font-size:13px;">${esc(SEGMENT_LABELS[segment] || segment)} — ${data.count}</strong>
        <span class="spacer" style="flex:1;"></span>
        <button type="button" class="btn ghost" id="crmSegmentCsv" style="font-size:11px; padding:2px 8px;">⬇ Export CSV</button>
        <button type="button" class="btn ghost" data-segment-close="1" style="font-size:11px; padding:2px 8px;">✕ Close</button>
      </div>
      <div class="table-wrap" style="max-height:340px; overflow:auto;">
        <table style="font-size:12px; width:100%;"><thead><tr>
          <th>Name</th><th>Phone</th><th>Region</th><th>State</th><th>Class</th><th>Plan</th>
          <th>Overdue</th><th>Days od</th><th>Days susp</th><th>GPS</th>
        </tr></thead><tbody>${rows || '<tr><td colspan="10" class="muted">No customers.</td></tr>'}</tbody></table>
      </div>
      ${items.length > 500 ? `<div class="muted" style="font-size:11px; margin-top:4px;">Showing first 500 of ${data.count}. Export CSV for the full list.</div>` : ''}`;
    const csvBtn = document.getElementById('crmSegmentCsv');
    if (csvBtn) {
      csvBtn.addEventListener('click', () => {
        const a = document.createElement('a');
        a.href = API.crmCustomerReportSegment(segment, 'csv');
        a.download = `crm_${segment}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      });
    }
  }

  async function loadCrmCustomerMap() {
    // Delegates to the shared renderer (js/lib/customer-map.js) so CRM and the
    // Tech Support map stay identical.
    await renderCustomerMap({ container: crmCustomerMapDiv, metaEl: crmCustomerMapMeta });
  }

  function openCrmCustomerDialog(item) {
    if (!crmCustomerDialog) { console.warn('crm: #crmCustomerDlg missing.'); return; }
    crmEditingCustomer = item || null;
    if (crmCustomerForm) crmCustomerForm.reset();
    if (crmCustomerNameInput) crmCustomerNameInput.value = (item && item.display_name) || '';
    if (crmCustomerSegmentInput) crmCustomerSegmentInput.value = (item && item.segment) || '';
    if (crmCustomerPhoneInput) crmCustomerPhoneInput.value = (item && item.primary_phone) || '';
    if (crmCustomerEmailInput) crmCustomerEmailInput.value = (item && item.primary_email) || '';
    if (crmCustomerAddressInput) crmCustomerAddressInput.value = (item && item.address) || '';
    if (crmCustomerCityInput) crmCustomerCityInput.value = (item && item.city) || '';
    if (crmCustomerRegionInput) crmCustomerRegionInput.value = (item && item.region) || '';
    const linked = _isRealPorta(item && item.porta_customer_id);
    if (crmCustomerPortaNote) {
      crmCustomerPortaNote.textContent = linked
        ? 'Contact changes will sync to PortaOne on save.'
        : 'No PortaOne account linked — changes are saved locally only.';
      crmCustomerPortaNote.style.display = '';
    }
    // Offer a quick link to PortaBilling for unlinked customers (edit roles).
    const linkPortaBtn = document.getElementById('crmCustomerLinkPortaBtn');
    if (linkPortaBtn) {
      const showLink = !linked && !!item && hasRole(..._CUSTOMER_EDIT_ROLES);
      linkPortaBtn.style.display = showLink ? '' : 'none';
      linkPortaBtn.onclick = showLink
        ? () => { closeCrmCustomerDialog(); openPortaLinkPicker(item); }
        : null;
    }
    openDialog(crmCustomerDialog);
    if (crmCustomerNameInput) crmCustomerNameInput.focus();
  }

  function closeCrmCustomerDialog() {
    if (!crmCustomerDialog) return;
    try { crmCustomerDialog.close(); }
    catch { crmCustomerDialog.open = false; }
  }

  async function loadCustomerExtras(item, container) {
    if (!container || container.dataset.extras === '1' || container.dataset.extrasLoading === '1') return;
    const billingEl = container.querySelector('[data-role="billing"]');
    const ticketsEl = container.querySelector('[data-role="tickets"]');
    if (!billingEl || !ticketsEl) return;
    container.dataset.extrasLoading = '1';
    billingEl.innerHTML = '<div class="crm-detail-loading">Loading billing…</div>';
    ticketsEl.innerHTML = '<div class="crm-detail-loading">Loading tickets…</div>';
    try {
      const detail = await adminAPI.fetch(API.crmCustomer(item.id), { method: 'GET' });
      renderCustomerExtras(container, item, detail);
      container.dataset.extras = '1';
    } catch (err) {
      billingEl.innerHTML = '<div class="crm-detail-error">Failed to load billing.</div>';
      ticketsEl.innerHTML = '<div class="crm-detail-error">Failed to load tickets.</div>';
    } finally {
      container.dataset.extrasLoading = '0';
    }
  }

  async function refreshCustomerBilling(item, billingEl) {
    const btn = billingEl.querySelector('[data-role="refresh-billing"]');
    if (btn) { btn.disabled = true; btn.textContent = 'Refreshing…'; }
    try {
      const billing = await adminAPI.fetch(API.crmCustomerRefreshBilling(item.id), { method: 'POST' });
      renderBillingBlock(billingEl, item, billing);
      showFlash('Billing refreshed');
    } catch (err) {
      showFlash((err && err.message) || 'Failed to refresh billing', 'error');
      if (btn) { btn.disabled = false; btn.textContent = '↻ Refresh'; }
    }
  }

  // ---- Link an unlinked customer to a PortaBilling profile ----------------
  let _portaLinkTarget = null;
  let _portaLinkTimer = null;

  function openPortaLinkPicker(item, { afterLink } = {}) {
    const dlg = document.getElementById('crmCustomerLinkDlg');
    if (!dlg || !item) return;
    _portaLinkTarget = { item, afterLink: afterLink || null };
    const title = document.getElementById('crmCustomerLinkTitle');
    if (title) title.textContent = `Link "${item.display_name || 'customer'}" to PortaBilling`;
    const input = document.getElementById('crmCustomerLinkSearch');
    const prefill = item.primary_phone || item.display_name || '';
    if (input) input.value = prefill;
    openDialog(dlg);
    runCustomerPortaSearch(prefill);
  }

  async function runCustomerPortaSearch(q) {
    const box = document.getElementById('crmCustomerLinkResults');
    if (!box || !_portaLinkTarget) return;
    const query = (q || '').trim();
    if (query.length < 3) { box.textContent = 'Type at least 3 characters…'; return; }
    box.textContent = 'Searching PortaBilling…';
    // Single free-text query — the backend partial-matches it against the
    // customer name (which also covers a number embedded in the name).
    const params = new URLSearchParams();
    params.set('q', query);
    try {
      const res = await adminAPI.fetch(API.crmCustomerPortaSearch(params.toString()), { method: 'GET' });
      const items = (res && res.items) || [];
      if (!items.length) { box.innerHTML = '<span class="crm-detail-empty">No active Porta customers found.</span>'; return; }
      box.innerHTML = '';
      items.forEach((it) => {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex; align-items:center; gap:8px; padding:4px 0; border-bottom:1px solid var(--border,#eee);';
        const info = document.createElement('div');
        info.style.cssText = 'flex:1; font-size:13px;';
        info.innerHTML = `<strong>#${escapeHtml(it.i_customer)}</strong> ${escapeHtml(it.name || '')} <span class="crm-detail-sub">${escapeHtml(it.phone || '')}</span>`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn primary';
        btn.style.cssText = 'font-size:11px; padding:2px 8px;';
        btn.textContent = 'Link';
        btn.addEventListener('click', () => withButtonBusy(btn, () => linkCustomerToPorta(it.i_customer)));
        row.appendChild(info);
        row.appendChild(btn);
        box.appendChild(row);
      });
    } catch (err) {
      box.innerHTML = `<span class="crm-detail-error">${escapeHtml((err && err.message) || 'Search failed')}</span>`;
    }
  }

  async function linkCustomerToPorta(portaId, { confirmMerge = false } = {}) {
    if (!_portaLinkTarget || !portaId) return;
    const { item, afterLink } = _portaLinkTarget;
    try {
      const billing = await adminAPI.fetch(API.crmCustomerLink(item.id), {
        method: 'POST',
        body: JSON.stringify({ porta_customer_id: String(portaId), confirm_merge: confirmMerge }),
      });
      item.porta_customer_id = String(portaId);
      showFlash(confirmMerge
        ? 'Merged into the existing PortaBilling-linked customer.'
        : `Linked to Porta #${portaId}.`);
      const dlg = document.getElementById('crmCustomerLinkDlg');
      if (dlg) closeDialog(dlg);
      // On merge this customer record is gone — just refresh; don't re-render it.
      if (!confirmMerge && typeof afterLink === 'function') afterLink(billing);
      // Refresh the list so the "Unlinked only" filter drops it.
      loadCrmCustomers();
    } catch (err) {
      const detail = err && err.body && err.body.detail;
      if (err && err.status === 409 && detail && detail.error === 'porta_id_already_linked_elsewhere') {
        const holderName = detail.holder_name || 'another customer';
        const proceed = window.confirm(
          `PortaBilling #${portaId} is already linked to "${holderName}".\n\n` +
          `Linking will MERGE "${item.display_name || 'this customer'}" into "${holderName}" — ` +
          `their tickets, enquiries and history are combined and this duplicate record is removed.\n\nProceed?`
        );
        if (proceed) return linkCustomerToPorta(portaId, { confirmMerge: true });
        return;
      }
      showFlash((err && err.message) || 'Failed to link customer', 'error');
    }
  }

  function createCrmExpandButton(kind, item, detailRow, detailContainer, label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'crm-expand-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', detailContainer.id);
    if (label) btn.setAttribute('aria-label', label);
    const icon = document.createElement('span');
    icon.className = 'crm-expand-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '▸';
    btn.appendChild(icon);
    btn.addEventListener('click', (event) => {
      event.stopPropagation();
      toggleCrmEnquiryDetail(kind, item, btn, detailRow, detailContainer);
    });
    return btn;
  }

  async function toggleCrmEnquiryDetail(kind, item, control, detailRow, detailContainer) {
    const isExpanded = control.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      control.setAttribute('aria-expanded', 'false');
      detailRow.hidden = true;
      return;
    }

    control.setAttribute('aria-expanded', 'true');
    detailRow.hidden = false;

    // Customers get billing + open tickets above their enquiries.
    if (kind === 'customer') {
      loadCustomerExtras(item, detailContainer);
    }

    // Prospect drawer gets a Quotations section (mounted once).
    const quotesEl = detailContainer.querySelector('[data-role="quotes"]');
    if (quotesEl && detailContainer.dataset.quotesLoaded !== '1') {
      detailContainer.dataset.quotesLoaded = '1';
      quotes.mountQuotesSection(quotesEl, item);
    }

    if (detailContainer.dataset.loaded === '1' || detailContainer.dataset.loading === '1') {
      return;
    }

    const cache = kind === 'customer' ? crmCustomerDetailCache : crmProspectDetailCache;
    const cachedItems = cache.get(item.id);
    if (Array.isArray(cachedItems)) {
      renderCrmEnquiriesInto(detailContainer, cachedItems);
      detailContainer.dataset.loaded = '1';
      return;
    }

    // Target the enquiries body by role: the customer markup has other
    // .crm-detail-body elements (billing/tickets) that must not be hijacked.
    const body = detailContainer.querySelector('[data-role="enquiries-body"]');
    if (body) {
      body.innerHTML = '<div class="crm-detail-loading">Loading enquiries…</div>';
    }
    detailContainer.dataset.loading = '1';

    const params = new URLSearchParams();
    params.set('limit', '10');
    params.set('offset', '0');
    if (kind === 'customer') {
      params.set('support_customer_id', item.id);
    } else {
      params.set('prospect_id', item.id);
    }

    try {
      const data = await adminAPI.fetch(API.crmEnquiries(params.toString()), { method: 'GET' });
      const enquiries = Array.isArray(data?.items) ? data.items : [];
      cache.set(item.id, enquiries);
      renderCrmEnquiriesInto(detailContainer, enquiries);
      detailContainer.dataset.loaded = '1';
    } catch (err) {
      console.warn('crm_enquiries_load_failed', err);
      if (body) {
        body.innerHTML = '<div class="crm-detail-error">Failed to load enquiries.</div>';
      }
      detailContainer.dataset.loaded = '0';
    } finally {
      detailContainer.dataset.loading = '0';
    }
  }

  function renderCrmEnquiriesInto(container, enquiries) {
    // Target the enquiries body by role (the customer markup has other
    // .crm-detail-body elements for billing/tickets).
    const body = container.querySelector('[data-role="enquiries-body"]');
    if (!body) return;
    if (!Array.isArray(enquiries) || !enquiries.length) {
      body.innerHTML = '<div class="crm-detail-empty">No enquiries yet.</div>';
      return;
    }
    const list = document.createElement('ul');
    list.className = 'crm-enquiry-list crm-scroll-cap';
    const orderedEnquiries = enquiries.slice().sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    orderedEnquiries.forEach((enquiry) => {
      const item = document.createElement('li');
      item.className = 'crm-enquiry-item';

      const head = document.createElement('div');
      head.className = 'crm-enquiry-item-head';
      const statusLabel = formatCrmLabel(enquiry.status);
      if (enquiry.status) {
        const statusBadge = document.createElement('span');
        statusBadge.className = `crm-enquiry-status ${crmStatusClass(enquiry.status)}`;
        statusBadge.textContent = statusLabel || enquiry.status;
        head.appendChild(statusBadge);
      }
      if (enquiry.enquiry_type) {
        const typeSpan = document.createElement('span');
        typeSpan.className = 'crm-enquiry-type';
        typeSpan.textContent = formatCrmLabel(enquiry.enquiry_type);
        head.appendChild(typeSpan);
      }
      if (enquiry.channel) {
        const channelSpan = document.createElement('span');
        channelSpan.className = 'crm-enquiry-channel';
        channelSpan.textContent = formatCrmLabel(enquiry.channel);
        head.appendChild(channelSpan);
      }
      if (enquiry.created_at) {
        const dateSpan = document.createElement('span');
        dateSpan.className = 'crm-enquiry-date';
        dateSpan.textContent = formatDateTimeShort(enquiry.created_at);
        head.appendChild(dateSpan);
      }
      item.appendChild(head);

      const bodyWrap = document.createElement('div');
      bodyWrap.className = 'crm-enquiry-item-body';

      const title = document.createElement('strong');
      const subjectSource = enquiry.subject || enquiry.description || enquiry.message_body || '';
      title.textContent = subjectSource ? truncate(subjectSource, 120) : 'Enquiry';
      bodyWrap.appendChild(title);

      const snippetSource = enquiry.message_body && enquiry.message_body !== enquiry.subject
        ? enquiry.message_body
        : enquiry.description && enquiry.description !== enquiry.subject
          ? enquiry.description
          : '';
      if (snippetSource) {
        const snippet = document.createElement('p');
        snippet.className = 'crm-enquiry-snippet';
        snippet.textContent = truncate(snippetSource, 160);
        bodyWrap.appendChild(snippet);
      }

      const metaParts = [];
      if (enquiry.contact_name) metaParts.push(enquiry.contact_name);
      if (enquiry.contact_phone) metaParts.push(enquiry.contact_phone);
      if (enquiry.contact_email) metaParts.push(enquiry.contact_email);
      if (metaParts.length) {
        const metaLine = document.createElement('span');
        metaLine.className = 'crm-enquiry-meta';
        metaLine.textContent = metaParts.join(' · ');
        bodyWrap.appendChild(metaLine);
      }

      // Body (title/snippet/meta) on the left; recorded notes panel on the right.
      const mainRow = document.createElement('div');
      mainRow.className = 'crm-enquiry-main';
      mainRow.appendChild(bodyWrap);
      const notesText = enquiry.recorded_notes && String(enquiry.recorded_notes).trim();
      if (notesText) {
        const notes = document.createElement('div');
        notes.className = 'crm-enquiry-notes';
        const label = document.createElement('span');
        label.className = 'crm-enquiry-notes-label';
        label.textContent = 'Notes';
        const text = document.createElement('p');
        text.className = 'crm-enquiry-notes-text';
        text.textContent = notesText;
        notes.appendChild(label);
        notes.appendChild(text);
        mainRow.appendChild(notes);
      }

      item.appendChild(mainRow);
      list.appendChild(item);
    });
    body.innerHTML = '';
    body.appendChild(list);
  }

  export function formatCrmLabel(value) {
    if (!value) return '';
    return String(value)
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function sanitizeClassSegment(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  export function crmStatusClass(value) {
    const segment = sanitizeClassSegment(value);
    return segment ? `status-${segment}` : '';
  }

  function truncate(value, maxLength) {
    if (!value) return '';
    const str = String(value).trim();
    if (!maxLength || str.length <= maxLength) return str;
    return `${str.slice(0, Math.max(0, maxLength - 1))}…`;
  }

  // ---- Customers/Prospects render --------------------------------------
  function renderCrmCustomers() {
    if (!crmCustomersTbody) return;
    const items = Array.isArray(crmState.customers) ? crmState.customers : [];
    crmCustomersTbody.innerHTML = '';
    if (!items.length) {
      crmCustomersTbody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">No customers found.</td></tr>';
      updateCrmCustomersPagination();
      return;
    }
    items.forEach((item, index) => {
      const detailContainerId = `crmCustomerDetail-${item.id}`;
      const detailRow = document.createElement('tr');
      detailRow.className = 'crm-detail-row';
      detailRow.hidden = true;
      const detailCell = document.createElement('td');
      detailCell.colSpan = 5;
      detailCell.innerHTML = createCrmCustomerDetailMarkup(detailContainerId);
      detailRow.appendChild(detailCell);
      const detailContainer = detailCell.querySelector('.crm-detail');

      const row = document.createElement('tr');
      row.className = 'crm-row';
      const rowNumber = crmState.customersOffset + index + 1;

      const toggleCell = document.createElement('td');
      const toggleWrap = document.createElement('div');
      toggleWrap.className = 'crm-row-toggle';
      const toggleBtn = createCrmExpandButton(
        'customer',
        item,
        detailRow,
        detailContainer,
        `Toggle enquiries for ${item.display_name || 'customer'}`
      );
      const numberSpan = document.createElement('span');
      numberSpan.className = 'crm-row-number';
      numberSpan.textContent = rowNumber;
      toggleWrap.appendChild(toggleBtn);
      toggleWrap.appendChild(numberSpan);
      toggleCell.appendChild(toggleWrap);
      row.appendChild(toggleCell);

      const nameCell = document.createElement('td');
      nameCell.textContent = item.display_name || '';
      // Flag customers whose latest billing account is suspended/blocked.
      const acctState = (item.account_state || '').toLowerCase();
      const inactive = acctState === 'suspended' || acctState === 'blocked';
      if (inactive) {
        row.style.background = '#fff5f5';
        const badge = document.createElement('span');
        badge.textContent = acctState === 'blocked' ? 'Blocked' : 'Suspended';
        badge.title = 'Billing account is not active';
        badge.style.cssText = 'margin-left:8px;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;color:#b91c1c;background:#fee2e2;';
        nameCell.appendChild(badge);
      }
      row.appendChild(nameCell);

      const contactCell = document.createElement('td');
      const contactParts = [];
      if (item.primary_phone) contactParts.push(item.primary_phone);
      if (item.primary_email) contactParts.push(item.primary_email);
      contactCell.textContent = contactParts.length ? contactParts.join(' · ') : '—';
      row.appendChild(contactCell);

      const addressCell = document.createElement('td');
      const addressParts = [];
      if (item.address) addressParts.push(item.address);
      if (item.city) addressParts.push(item.city);
      if (item.region) addressParts.push(item.region);
      addressCell.textContent = addressParts.length ? addressParts.join(', ') : '—';
      row.appendChild(addressCell);

      const createdCell = document.createElement('td');
      createdCell.textContent = item.created_at ? formatDateTimeShort(item.created_at) : '';
      row.appendChild(createdCell);

      crmCustomersTbody.appendChild(row);
      crmCustomersTbody.appendChild(detailRow);
    });
    updateCrmCustomersPagination();
  }

  // Conversion-likelihood meter for new/qualified prospects, based on how long
  // they have been in their current status (status_changed_at, falling back to
  // created_at): <1 week Hot, <3 weeks Warm, otherwise Cold. Not shown for
  // converted/disqualified (returns null).
  function prospectMeter(item) {
    const status = (item && item.status) || '';
    if (status !== 'new' && status !== 'qualified') return null;
    const basis = (item && (item.status_changed_at || item.created_at)) || null;
    const ts = basis ? Date.parse(basis) : NaN;
    if (Number.isNaN(ts)) return null;
    const days = (Date.now() - ts) / 86400000;
    if (days < 7) return { label: 'Hot', color: '#C62828', bg: '#FDECEA' };
    if (days < 21) return { label: 'Warm', color: '#E65100', bg: '#FFF3E0' };
    return { label: 'Cold', color: '#1565C0', bg: '#E3F2FD' };
  }

  function updateProspectTabsActive() {
    if (!crmProspectTabs) return;
    const current = crmState.prospectsStatus || '';
    crmProspectTabs.querySelectorAll('button[data-status]').forEach((btn) => {
      const active = (btn.getAttribute('data-status') || '') === current;
      btn.classList.toggle('primary', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  // Per-owner pipeline chart: who owns leads, their Hot/Warm/Cold split and a
  // per-status breakdown. Hand-rolled segmented bars (same spirit as the tech
  // support resolution chart) — no chart library.
  function renderProspectPipeline(summary) {
    if (!crmProspectPipeline) return;
    const owners = summary && Array.isArray(summary.owners) ? summary.owners : [];
    crmProspectPipeline.innerHTML = '';
    if (!owners.length) {
      crmProspectPipeline.innerHTML = '<div style="color:var(--sub); font-size:12px;">No prospects yet.</div>';
      return;
    }
    const COLORS = { hot: '#C62828', warm: '#E65100', cold: '#1565C0' };
    const legend = document.createElement('div');
    legend.style.cssText = 'display:flex; gap:14px; margin-bottom:10px; font-size:11px; color:var(--sub);';
    legend.innerHTML = [['Hot', COLORS.hot], ['Warm', COLORS.warm], ['Cold', COLORS.cold]]
      .map(([label, color]) => `<span style="display:inline-flex; align-items:center; gap:5px;"><span style="width:10px; height:10px; border-radius:2px; background:${color}; display:inline-block;"></span>${label}</span>`)
      .join('');
    crmProspectPipeline.appendChild(legend);

    owners.forEach((owner) => {
      const active = Number(owner.active || 0);
      const hot = Number(owner.hot || 0);
      const warm = Number(owner.warm || 0);
      const cold = Number(owner.cold || 0);
      const row = document.createElement('div');
      row.style.cssText = 'display:grid; grid-template-columns:150px 1fr auto; gap:10px; align-items:center; margin-bottom:6px;';

      const name = document.createElement('div');
      name.style.cssText = 'font-size:13px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;';
      name.textContent = owner.owner_name || 'Unassigned';
      name.title = `${owner.owner_name || 'Unassigned'} — ${active} active of ${owner.total || 0} total`;

      const bar = document.createElement('div');
      bar.style.cssText = 'display:flex; height:14px; border-radius:7px; overflow:hidden; background:var(--border,#eee);';
      const addSeg = (count, color) => {
        if (!count) return;
        const seg = document.createElement('div');
        seg.style.cssText = `background:${color}; flex:${count} 0 0;`;
        seg.title = String(count);
        bar.appendChild(seg);
      };
      if (active > 0) {
        addSeg(hot, COLORS.hot);
        addSeg(warm, COLORS.warm);
        addSeg(cold, COLORS.cold);
      }

      const meta = document.createElement('div');
      meta.style.cssText = 'font-size:11px; color:var(--sub); white-space:nowrap;';
      const bs = owner.by_status || {};
      const stageOrder = [['new', 'New'], ['contacted', 'Contacted'], ['qualified', 'Qualified'], ['converted', 'Converted'], ['disqualified', 'Disqualified']];
      const stageParts = stageOrder.filter(([key]) => bs[key]).map(([key, label]) => `${label} ${bs[key]}`);
      meta.textContent = `${hot}/${warm}/${cold} H·W·C${stageParts.length ? ` — ${stageParts.join(' · ')}` : ''}`;

      row.appendChild(name);
      row.appendChild(bar);
      row.appendChild(meta);
      crmProspectPipeline.appendChild(row);
    });
  }

  async function loadProspectPipeline() {
    if (!crmProspectPipeline) return;
    try {
      const data = await adminAPI.fetch(API.crmProspectsSummary(), { method: 'GET' });
      renderProspectPipeline(data);
    } catch (err) {
      crmProspectPipeline.innerHTML = `<div style="color:var(--err); font-size:12px;">${(err && err.message) || 'Failed to load lead ownership'}</div>`;
    }
  }

  function renderCrmProspects() {
    if (!crmProspectsTbody) return;
    const items = Array.isArray(crmState.prospects) ? crmState.prospects : [];
    crmProspectsTbody.innerHTML = '';
    if (!items.length) {
      crmProspectsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No prospects recorded yet.</td></tr>';
      updateCrmProspectsPagination();
      return;
    }
    // Group the worklist into Overdue / Due today / Upcoming / No follow-up
    // sections so reps work the list top-down. Sort by bucket then by the
    // scheduled time within each bucket (soonest first).
    const grouped = items
      .map((item) => ({ item, bucket: followUpBucket(item) }))
      .sort((a, b) => (a.bucket.rank - b.bucket.rank) || (a.bucket.ts - b.bucket.ts));
    let currentBucketKey = null;
    grouped.forEach(({ item, bucket }, index) => {
      if (bucket.key !== currentBucketKey) {
        currentBucketKey = bucket.key;
        const count = grouped.filter((g) => g.bucket.key === bucket.key).length;
        const style = BUCKET_STYLES[bucket.key] || BUCKET_STYLES.none;
        const headerRow = document.createElement('tr');
        headerRow.className = 'crm-worklist-section';
        const headerCell = document.createElement('td');
        headerCell.colSpan = 8;
        headerCell.style.cssText = `padding:6px 10px;font-size:12px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:${style.color};background:${style.bg};border-top:1px solid var(--line,#e5e7eb);`;
        headerCell.textContent = `${bucket.label} (${count})`;
        headerRow.appendChild(headerCell);
        crmProspectsTbody.appendChild(headerRow);
      }
      const detailContainerId = `crmProspectDetail-${item.id}`;
      const detailRow = document.createElement('tr');
      detailRow.className = 'crm-detail-row';
      detailRow.hidden = true;
      const detailCell = document.createElement('td');
      detailCell.colSpan = 8;
      detailCell.innerHTML = createCrmDetailMarkup(detailContainerId);
      detailRow.appendChild(detailCell);
      const detailContainer = detailCell.querySelector('.crm-detail');

      const row = document.createElement('tr');
      row.className = 'crm-row';
      const rowNumber = crmState.prospectsOffset + index + 1;

      const toggleCell = document.createElement('td');
      const toggleWrap = document.createElement('div');
      toggleWrap.className = 'crm-row-toggle';
      const toggleBtn = createCrmExpandButton(
        'prospect',
        item,
        detailRow,
        detailContainer,
        `Toggle enquiries for ${item.full_name || 'prospect'}`
      );
      const numberSpan = document.createElement('span');
      numberSpan.className = 'crm-row-number';
      numberSpan.textContent = rowNumber;
      toggleWrap.appendChild(toggleBtn);
      toggleWrap.appendChild(numberSpan);
      toggleCell.appendChild(toggleWrap);
      row.appendChild(toggleCell);

      const nameCell = document.createElement('td');
      const nameBtn = document.createElement('button');
      nameBtn.type = 'button';
      nameBtn.title = 'Edit prospect';
      nameBtn.textContent = item.full_name || '(unnamed)';
      nameBtn.style.cssText = 'background:none;border:none;padding:0;font:inherit;color:var(--link,#2563eb);text-align:left;text-decoration:underline;cursor:pointer;';
      nameBtn.addEventListener('click', () => openCrmProspectDialog(item));
      nameCell.appendChild(nameBtn);
      row.appendChild(nameCell);

      const contactCell = document.createElement('td');
      buildContactLinks(contactCell, item);
      row.appendChild(contactCell);

      const statusCell = document.createElement('td');
      statusCell.textContent = formatCrmLabel(item.status || '');
      const meter = prospectMeter(item);
      if (meter) {
        const badge = document.createElement('span');
        badge.textContent = meter.label;
        badge.title = 'Conversion likelihood (based on time in current status)';
        badge.style.cssText = `margin-left:8px;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meter.color};background:${meter.bg};`;
        statusCell.appendChild(badge);
      }
      row.appendChild(statusCell);

      const followCell = document.createElement('td');
      const fu = formatFollowUp(item);
      followCell.textContent = fu.text;
      followCell.title = fu.title;
      if (fu.text !== '—') followCell.style.color = fu.color;
      row.appendChild(followCell);

      const cityCell = document.createElement('td');
      cityCell.textContent = item.city || '—';
      row.appendChild(cityCell);

      const ownerCell = document.createElement('td');
      const myId = currentUserId();
      const isMine = myId != null && item.owner_user_id != null && Number(item.owner_user_id) === myId;
      // Surface the current owner (name + a subtle "(you)") so the list stays
      // scannable for follow-up.
      if (item.owner_user_id != null) {
        const ownerName = item.owner_name || `User ${item.owner_user_id}`;
        const badge = document.createElement('span');
        badge.textContent = ownerName;
        badge.title = isMine ? 'Assigned to you' : `Assigned to ${ownerName}`;
        badge.style.cssText = 'display:inline-block;padding:1px 8px;border-radius:10px;font-size:12px;font-weight:600;color:#3730a3;background:#eef2ff;';
        ownerCell.appendChild(badge);
        if (isMine) {
          const you = document.createElement('span');
          you.textContent = ' (you)';
          you.style.cssText = 'color:var(--sub);font-size:11px;margin-left:4px;';
          ownerCell.appendChild(you);
        }
      }
      // Quick self-assign for sales reps/supervisors. Visibility mirrors the
      // backend ownership policy so the button never 403s:
      //   • supervisors/root may reassign any prospect to themselves;
      //   • anyone here may claim an unassigned prospect;
      //   • sales reps may take a prospect over from customer care.
      if (!isMine) {
        const canFreeReassign = hasRole('sales_supervisor', 'root');
        const salesRep = hasRole('sales_officer', 'sales_supervisor');
        const unassigned = item.owner_user_id == null;
        let canSelfAssign = false;
        if (canFreeReassign) canSelfAssign = true;
        else if (unassigned) canSelfAssign = true;
        else if (item.owner_is_customer_care && salesRep) canSelfAssign = true;
        if (canSelfAssign) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'btn ghost small';
          if (!unassigned) btn.style.marginLeft = '8px';
          btn.textContent = unassigned ? 'Assign to me' : 'Take over';
          btn.title = unassigned ? 'Claim this prospect' : 'Reassign this prospect to you';
          btn.addEventListener('click', () => assignProspectToMe(item));
          ownerCell.appendChild(btn);
        }
      }
      if (!ownerCell.childElementCount) {
        ownerCell.textContent = '—';
      }
      row.appendChild(ownerCell);

      const actionsCell = document.createElement('td');
      const logBtn = document.createElement('button');
      logBtn.type = 'button';
      logBtn.className = 'btn ghost small';
      logBtn.textContent = '📞 Log';
      logBtn.title = 'Log a contact / set the next follow-up';
      logBtn.addEventListener('click', () => openLogContactDialog(item, { afterSave: loadCrmProspects }));
      actionsCell.appendChild(logBtn);
      row.appendChild(actionsCell);

      crmProspectsTbody.appendChild(row);
      crmProspectsTbody.appendChild(detailRow);
    });
    updateCrmProspectsPagination();
  }

  function updateCrmProspectsPagination() {
    if (crmProspectsPageInfo) {
      const total = Number(crmState.prospectsTotal || 0);
      const count = crmState.prospects.length || 0;
      const offset = crmState.prospectsOffset || 0;
      if (!total) {
        crmProspectsPageInfo.textContent = crmState.prospectsSearch ? 'No results' : 'No prospects';
      } else {
        const start = offset + 1;
        const end = offset + count;
        const page = Math.floor(offset / CRM_PAGE_SIZE) + 1;
        const totalPages = Math.max(1, Math.ceil(total / CRM_PAGE_SIZE));
        crmProspectsPageInfo.textContent = `Showing ${start}-${end} of ${total} • Page ${page} / ${totalPages}`;
      }
    }
    const disablePrev = crmState.prospectsOffset <= 0 || crmState.loadingProspects;
    const disableNext = (crmState.prospectsOffset + CRM_PAGE_SIZE) >= (crmState.prospectsTotal || 0) || crmState.loadingProspects;
    if (crmProspectsPrevBtn) crmProspectsPrevBtn.disabled = disablePrev;
    if (crmProspectsNextBtn) crmProspectsNextBtn.disabled = disableNext;
  }

  function updateCrmCustomersPagination() {
    if (crmCustomersPageInfo) {
      const total = Number(crmState.customersTotal || 0);
      const count = crmState.customers.length || 0;
      const offset = crmState.customersOffset || 0;
      if (!total) {
        crmCustomersPageInfo.textContent = crmState.customersSearch ? 'No results' : 'No customers';
      } else {
        const start = offset + 1;
        const end = offset + count;
        const page = Math.floor(offset / CRM_PAGE_SIZE) + 1;
        const totalPages = Math.max(1, Math.ceil(total / CRM_PAGE_SIZE));
        crmCustomersPageInfo.textContent = `Showing ${start}-${end} of ${total} • Page ${page} / ${totalPages}`;
      }
    }
    const disablePrev = crmState.customersOffset <= 0 || crmState.loadingCustomers;
    const disableNext = (crmState.customersOffset + CRM_PAGE_SIZE) >= (crmState.customersTotal || 0) || crmState.loadingCustomers;
    if (crmCustomersPrevBtn) crmCustomersPrevBtn.disabled = disablePrev;
    if (crmCustomersNextBtn) crmCustomersNextBtn.disabled = disableNext;
  }

  async function loadCrmProspects() {
    if (!crmProspectsTbody) {
      mount();
    }
    if (!crmProspectsTbody) {
      // eslint-disable-next-line no-console
      console.warn('loadCrmProspects: #crmProspectsTbody missing — view will not load.');
      return;
    }
    if (crmState.loadingProspects) return;
    crmState.loadingProspects = true;
    updateReconcileBtnVisibility();
    updateCrmProspectsPagination();
    if (crmProspectsRefreshBtn) crmProspectsRefreshBtn.disabled = true;
    crmProspectsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>';
    try {
      const params = new URLSearchParams();
      params.set('limit', String(CRM_PAGE_SIZE));
      params.set('offset', String(crmState.prospectsOffset || 0));
      if (crmState.prospectsSearch) params.set('search', crmState.prospectsSearch);
      // "active" is a virtual tab = the in-play statuses, which is exactly what the
      // API returns by default — so it sends no status param (one source of truth:
      // CRMProspectStatus.in_play()). A real status tab sends that single status.
      // "All" (blank) must opt into closed leads explicitly.
      if (crmState.prospectsStatus === 'active') {
        // no param — the API already scopes to in-play leads
      } else if (crmState.prospectsStatus) {
        params.set('status', crmState.prospectsStatus);
      } else {
        params.set('include_closed', 'true');
      }
      // Sales prospects list: only enquiries about sales/upsell + other (hide
      // billing/service/installation/etc. logged by other teams). Manually
      // added prospects (no enquiry) are kept by the backend filter.
      ['sales', 'other'].forEach((t) => params.append('enquiry_type', t));
      const ownerId = resolveOwnerFilterId();
      if (ownerId != null) params.set('owner_user_id', String(ownerId));
      if (crmState.prospectsDue) params.set('due', crmState.prospectsDue);
      if (crmState.prospectsAttention) params.set('attention', 'true');
      if (crmState.prospectsPendingQuotes) params.set('pending_quotes', 'true');
      const data = await adminAPI.fetch(API.crmProspects(params.toString()), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      crmState.prospects = items;
      crmState.prospectsTotal = typeof data?.total === 'number' ? data.total : (crmState.prospectsSearch ? items.length : (crmState.prospectsOffset || 0) + items.length);
      renderCrmProspects();
      updateAttentionChip();
      // Refresh the ownership chart only while it's expanded (collapsed by
      // default to save screen space); it covers all prospects, not just the page.
      if (crmProspectPipelineCard && crmProspectPipelineCard.open) loadProspectPipeline();
    } catch (err) {
      crmState.prospects = [];
      crmProspectsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--err);">Failed to load prospects</td></tr>';
      showFlash(err?.message || 'Failed to load prospects', 'error');
    } finally {
      crmState.loadingProspects = false;
      if (crmProspectsRefreshBtn) crmProspectsRefreshBtn.disabled = false;
      updateCrmProspectsPagination();
    }
  }

  async function loadCrmCustomers() {
    // Self-heal: when the view router calls us before `mount()` has
    // populated the local refs, the silent early-return below left the
    // tbody stuck on its HTML default "Loading…" forever — no fetch, no
    // console error. Calling mount() first makes the load idempotent
    // with respect to boot ordering. The warn keeps the next silent
    // breakage visible.
    if (!crmCustomersTbody) {
      mount();
    }
    if (!crmCustomersTbody) {
      // eslint-disable-next-line no-console
      console.warn('loadCrmCustomers: #crmCustomersTbody missing — view will not load.');
      return;
    }
    if (crmState.loadingCustomers) return;
    crmState.loadingCustomers = true;
    updateCrmCustomersPagination();
    if (crmCustomersRefreshBtn) crmCustomersRefreshBtn.disabled = true;
    crmCustomersTbody.innerHTML = '<tr><td colspan="5" style="color:var(--sub);">Loading…</td></tr>';
    try {
      const params = new URLSearchParams();
      params.set('limit', String(CRM_PAGE_SIZE));
      params.set('offset', String(crmState.customersOffset || 0));
      if (crmState.customersSearch) params.set('search', crmState.customersSearch);
      if (crmState.customersSegment) params.set('segment', crmState.customersSegment);
      params.set('status', crmState.customersStatus || 'active');
      if (crmState.customersKind) params.set('kind', crmState.customersKind);
      if (crmState.customersInactive) params.set('inactive', 'true');
      if (crmState.customersUnlinked) params.set('unlinked', 'true');
      const data = await adminAPI.fetch(API.crmCustomers(params.toString()), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      crmState.customers = items;
      crmState.customersTotal = typeof data?.total === 'number' ? data.total : (crmState.customersSearch ? items.length : (crmState.customersOffset || 0) + items.length);
      renderCrmCustomers();
    } catch (err) {
      crmState.customers = [];
      crmCustomersTbody.innerHTML = '<tr><td colspan="5" style="color:var(--err);">Failed to load customers</td></tr>';
      showFlash(err?.message || 'Failed to load customers', 'error');
    } finally {
      crmState.loadingCustomers = false;
      if (crmCustomersRefreshBtn) crmCustomersRefreshBtn.disabled = false;
      updateCrmCustomersPagination();
    }
  }

  // Prefill the dialog for create (prospect = null) or edit (prospect object).
  function setCrmProspectDialogMode(prospect) {
    crmEditingProspectId = prospect ? prospect.id : null;
    crmEditingProspect = prospect || null;
    if (crmProspectForm) crmProspectForm.reset();
    if (crmProspectDlgTitle) crmProspectDlgTitle.textContent = prospect ? 'Edit prospect' : 'New prospect';
    if (crmProspectSubmitBtn) crmProspectSubmitBtn.textContent = prospect ? 'Save changes' : 'Save prospect';
    if (crmProspectNameInput) crmProspectNameInput.value = (prospect && prospect.full_name) || '';
    if (crmProspectStatusInput) crmProspectStatusInput.value = (prospect && prospect.status) || 'new';
    if (crmProspectSourceInput) crmProspectSourceInput.value = (prospect && prospect.source) || 'unknown';
    if (crmProspectPhoneInput) crmProspectPhoneInput.value = (prospect && prospect.phone_number) || '';
    if (crmProspectEmailInput) crmProspectEmailInput.value = (prospect && prospect.email) || '';
    if (crmProspectCityInput) crmProspectCityInput.value = (prospect && prospect.city) || '';
    if (crmProspectAddressInput) crmProspectAddressInput.value = (prospect && prospect.street_address) || '';
    if (crmProspectCompanyInput) crmProspectCompanyInput.value = (prospect && prospect.company) || '';
    if (crmProspectTagsInput) {
      crmProspectTagsInput.value = Array.isArray(prospect && prospect.tags) ? prospect.tags.join(', ') : '';
    }
    if (crmProspectNotesInput) crmProspectNotesInput.value = (prospect && prospect.notes) || '';
    // Status only applies to existing prospects; new ones always start as "new".
    const statusField = crmProspectStatusInput ? crmProspectStatusInput.closest('.field') : null;
    if (statusField) statusField.style.display = prospect ? '' : 'none';
    if (crmProspectLastContacted) {
      if (prospect && prospect.last_contacted_at) {
        crmProspectLastContacted.textContent = `Last contacted: ${formatDateTimeShort(prospect.last_contacted_at)}`;
        crmProspectLastContacted.style.display = '';
      } else {
        crmProspectLastContacted.textContent = '';
        crmProspectLastContacted.style.display = 'none';
      }
    }
    // Reassigning to a specific rep is a supervisor/root action, edit-mode only.
    if (crmProspectOwnerField && crmProspectOwnerInput) {
      const showOwner = !!prospect && isProspectSupervisor();
      crmProspectOwnerField.style.display = showOwner ? '' : 'none';
      if (showOwner) {
        fillOwnerOptions(crmProspectOwnerInput);
        crmProspectOwnerInput.value = prospect && prospect.owner_user_id != null
          ? String(prospect.owner_user_id)
          : '';
      }
    }
    // Log-contact button + activity timeline only make sense for existing leads.
    const logBtn = document.getElementById('crmProspectLogContactBtn');
    const confirmBtn = document.getElementById('crmProspectConfirmBtn');
    const timelineWrap = document.getElementById('crmProspectTimelineWrap');
    const dupWarn = document.getElementById('crmProspectDupWarning');
    if (logBtn) logBtn.style.display = prospect ? '' : 'none';
    // Confirm-for-installation is available for existing leads not yet converted.
    if (confirmBtn) {
      const show = !!prospect && prospect.status !== 'converted';
      confirmBtn.style.display = show ? '' : 'none';
      if (show) {
        confirmBtn.textContent = prospect.install_ready
          ? '✅ Confirmed — re-confirm'
          : '✅ Confirm for installation';
      }
    }
    if (dupWarn) { dupWarn.style.display = 'none'; dupWarn.textContent = ''; }
    if (prospect) {
      loadProspectTimeline(prospect.id);
    } else if (timelineWrap) {
      timelineWrap.style.display = 'none';
    }
  }

  function openCrmProspectDialog(prospect) {
    if (!crmProspectDialog) { console.warn('crm: #crmProspectDlg missing.'); return; }
    setCrmProspectDialogMode(prospect || null);
    openDialog(crmProspectDialog);
    if (crmProspectNameInput) crmProspectNameInput.focus();
  }

  function closeCrmProspectDialog() {
    if (!crmProspectDialog) return;
    try { crmProspectDialog.close(); }
    catch { crmProspectDialog.open = false; }
  }

  function closeCrmReconcileDialog() {
    if (!crmReconcileDialog) return;
    try { crmReconcileDialog.close(); }
    catch { crmReconcileDialog.open = false; }
  }

  // Root-only button; recomputed on each load so it appears once the current
  // user is known even if mount() ran earlier.
  function updateReconcileBtnVisibility() {
    if (crmProspectsReconcileBtn) {
      crmProspectsReconcileBtn.style.display = hasRole('root') ? '' : 'none';
    }
  }

  // Root-only: scan active prospects for a phone match against an existing
  // customer, preview the matches, and (on confirm) mark them converted.
  async function openCrmReconcile() {
    if (!crmReconcileDialog) { console.warn('crm: #crmReconcileDlg missing.'); return; }
    crmReconcileMatches = [];
    if (crmReconcileSummary) crmReconcileSummary.textContent = 'Scanning…';
    if (crmReconcileList) crmReconcileList.innerHTML = '';
    if (crmReconcileConfirm) crmReconcileConfirm.disabled = true;
    openDialog(crmReconcileDialog);
    try {
      const data = await adminAPI.fetch(API.crmProspectsReconcilePreview(), { method: 'GET' });
      const matches = Array.isArray(data && data.matches) ? data.matches : [];
      crmReconcileMatches = matches;
      if (crmReconcileSummary) {
        crmReconcileSummary.textContent = `Scanned ${data.scanned || 0} active prospect(s) — ${matches.length} match an existing customer by phone.`;
      }
      if (crmReconcileList) {
        crmReconcileList.innerHTML = '';
        if (!matches.length) {
          crmReconcileList.innerHTML = '<div class="crm-detail-empty">Nothing to reconcile.</div>';
        } else {
          const ul = document.createElement('ul');
          ul.className = 'crm-enquiry-list';
          matches.forEach((m) => {
            const li = document.createElement('li');
            li.className = 'crm-enquiry-item';
            const who = document.createElement('div');
            who.innerHTML = `<strong>${escapeHtml(m.prospect_name || 'Prospect')}</strong>${m.prospect_phone ? ' · ' + escapeHtml(m.prospect_phone) : ''}`;
            const arrow = document.createElement('div');
            arrow.style.cssText = 'font-size:12px; color:var(--sub);';
            arrow.textContent = `→ ${m.customer_name || 'Customer'}${m.customer_phone ? ' · ' + m.customer_phone : ''}`;
            li.appendChild(who);
            li.appendChild(arrow);
            ul.appendChild(li);
          });
          crmReconcileList.appendChild(ul);
        }
      }
      if (crmReconcileConfirm) crmReconcileConfirm.disabled = matches.length === 0;
    } catch (err) {
      if (crmReconcileSummary) crmReconcileSummary.textContent = (err && err.message) || 'Failed to scan prospects';
    }
  }

  // ---- Mount -----------------------------------------------------------
  let mounted = false;
  function mount() {
    if (mounted) return;
    const d = resolveDom();
    // Populate local refs from resolved DOM so lifted code reads them.
    crmCustomersTbody = d.crmCustomersTbody;
    crmCustomersPageInfo = d.crmCustomersPageInfo;
    crmCustomersPrevBtn = d.crmCustomersPrevBtn;
    crmCustomersNextBtn = d.crmCustomersNextBtn;
    crmCustomerSearchInput = d.crmCustomerSearchInput;
    crmCustomersRefreshBtn = d.crmCustomersRefreshBtn;
    crmCustomerSegmentFilter = d.crmCustomerSegmentFilter;
    crmCustomerStatusFilter = d.crmCustomerStatusFilter;
    crmCustomerKindFilter = d.crmCustomerKindFilter;
    crmCustomerInactiveFilter = d.crmCustomerInactiveFilter;
    crmCustomerUnlinkedFilter = d.crmCustomerUnlinkedFilter;
    crmCustomersMapToggle = d.crmCustomersMapToggle;
    crmCustomersReportBtn = d.crmCustomersReportBtn;
    crmCustomerReport = d.crmCustomerReport;
    crmCustomerListWrap = d.crmCustomerListWrap;
    crmCustomersPager = d.crmCustomersPager;
    crmCustomerMapWrap = d.crmCustomerMapWrap;
    crmCustomerMapDiv = d.crmCustomerMapDiv;
    crmCustomerMapMeta = d.crmCustomerMapMeta;
    crmCustomerMapExport = d.crmCustomerMapExport;
    crmProspectsTbody = d.crmProspectsTbody;
    crmProspectsPageInfo = d.crmProspectsPageInfo;
    crmProspectsPrevBtn = d.crmProspectsPrevBtn;
    crmProspectsNextBtn = d.crmProspectsNextBtn;
    crmProspectSearchInput = d.crmProspectSearchInput;
    crmProspectsRefreshBtn = d.crmProspectsRefreshBtn;
    crmProspectsNewBtn = d.crmProspectsNewBtn;
    crmProspectsReconcileBtn = d.crmProspectsReconcileBtn;
    crmReconcileDialog = d.crmReconcileDialog;
    crmReconcileForm = d.crmReconcileForm;
    crmReconcileSummary = d.crmReconcileSummary;
    crmReconcileList = d.crmReconcileList;
    crmReconcileConfirm = d.crmReconcileConfirm;
    crmProspectTabs = d.crmProspectTabs;
    crmProspectOwnerFilter = d.crmProspectOwnerFilter;
    crmProspectPipelineCard = d.crmProspectPipelineCard;
    crmProspectPipeline = d.crmProspectPipeline;
    crmProspectDialog = d.crmProspectDialog;
    crmProspectForm = d.crmProspectForm;
    crmProspectDlgTitle = d.crmProspectDlgTitle;
    crmProspectSubmitBtn = d.crmProspectSubmitBtn;
    crmProspectNameInput = d.crmProspectNameInput;
    crmProspectStatusInput = d.crmProspectStatusInput;
    crmProspectSourceInput = d.crmProspectSourceInput;
    crmProspectPhoneInput = d.crmProspectPhoneInput;
    crmProspectEmailInput = d.crmProspectEmailInput;
    crmProspectCityInput = d.crmProspectCityInput;
    crmProspectAddressInput = d.crmProspectAddressInput;
    crmProspectCompanyInput = d.crmProspectCompanyInput;
    crmProspectTagsInput = d.crmProspectTagsInput;
    crmProspectNotesInput = d.crmProspectNotesInput;
    crmProspectLastContacted = d.crmProspectLastContacted;
    crmProspectOwnerField = d.crmProspectOwnerField;
    crmProspectOwnerInput = d.crmProspectOwnerInput;
    crmCustomerDialog = d.crmCustomerDialog;
    crmCustomerForm = d.crmCustomerForm;
    crmCustomerSubmitBtn = d.crmCustomerSubmitBtn;
    crmCustomerNameInput = d.crmCustomerNameInput;
    crmCustomerSegmentInput = d.crmCustomerSegmentInput;
    crmCustomerPhoneInput = d.crmCustomerPhoneInput;
    crmCustomerEmailInput = d.crmCustomerEmailInput;
    crmCustomerAddressInput = d.crmCustomerAddressInput;
    crmCustomerCityInput = d.crmCustomerCityInput;
    crmCustomerRegionInput = d.crmCustomerRegionInput;
    crmCustomerPortaNote = d.crmCustomerPortaNote;

    // Reps default to "My prospects" to shrink the main list; supervisors/root
    // see all owners by default and get a per-rep owner picker.
    crmState.prospectsOwner = isProspectSupervisor() ? '' : '__me__';

    if (crmCustomersRefreshBtn) {
      crmCustomersRefreshBtn.addEventListener('click', () => {
        loadCrmCustomers();
        refreshCustomerReportIfOpen();
      });
    }
    if (crmCustomersMapToggle) {
      crmCustomersMapToggle.addEventListener('click', () => toggleCrmCustomerMap());
    }
    if (crmCustomersReportBtn) {
      crmCustomersReportBtn.addEventListener('click', () => toggleCustomerReport());
    }
    if (crmCustomerReport) {
      crmCustomerReport.addEventListener('click', (e) => {
        const dim = e.target.closest('[data-churn-dim]');
        if (dim) {
          _churnDim = dim.getAttribute('data-churn-dim');
          if (_customerReportData) renderCustomerReport(_customerReportData);
          return;
        }
        if (e.target.closest('[data-segment-close]')) {
          const box = document.getElementById('crmCustomerSegmentDrill');
          if (box) box.innerHTML = '';
          return;
        }
        const seg = e.target.closest('[data-segment]');
        if (seg) openSegmentDrill(seg.getAttribute('data-segment'));
      });
    }
    if (crmCustomerMapExport) {
      crmCustomerMapExport.addEventListener('click', () => {
        const a = document.createElement('a');
        a.href = API.crmCustomerMapExport();
        a.download = 'crm_customer_locations.csv';
        document.body.appendChild(a); a.click(); a.remove();
      });
    }
    if (crmCustomerSearchInput) {
      crmCustomerSearchInput.addEventListener('input', () => {
        if (crmCustomerSearchTimer) clearTimeout(crmCustomerSearchTimer);
        crmCustomerSearchTimer = setTimeout(() => {
          crmState.customersSearch = crmCustomerSearchInput.value.trim();
          crmState.customersOffset = 0;
          loadCrmCustomers();
        }, 250);
      });
    }
    if (crmCustomerSegmentFilter) {
      crmCustomerSegmentFilter.addEventListener('change', () => {
        crmState.customersSegment = crmCustomerSegmentFilter.value || '';
        crmState.customersOffset = 0;
        loadCrmCustomers();
      });
    }
    if (crmCustomerStatusFilter) {
      crmCustomerStatusFilter.addEventListener('change', () => {
        crmState.customersStatus = crmCustomerStatusFilter.value || 'active';
        crmState.customersOffset = 0;
        loadCrmCustomers();
      });
    }
    if (crmCustomerKindFilter) {
      crmCustomerKindFilter.addEventListener('change', () => {
        crmState.customersKind = crmCustomerKindFilter.value || '';
        crmState.customersOffset = 0;
        loadCrmCustomers();
      });
    }
    if (crmCustomerInactiveFilter) {
      crmCustomerInactiveFilter.addEventListener('change', () => {
        crmState.customersInactive = !!crmCustomerInactiveFilter.checked;
        crmState.customersOffset = 0;
        loadCrmCustomers();
      });
    }
    if (crmCustomerUnlinkedFilter) {
      crmCustomerUnlinkedFilter.addEventListener('change', () => {
        crmState.customersUnlinked = !!crmCustomerUnlinkedFilter.checked;
        crmState.customersOffset = 0;
        loadCrmCustomers();
      });
    }
    const linkSearchInput = document.getElementById('crmCustomerLinkSearch');
    if (linkSearchInput) {
      linkSearchInput.addEventListener('input', () => {
        clearTimeout(_portaLinkTimer);
        const v = linkSearchInput.value;
        // Wait for a typing pause before hitting PortaBilling (one remote call
        // per pause, not per keystroke).
        _portaLinkTimer = setTimeout(() => runCustomerPortaSearch(v), 700);
      });
    }
    if (crmCustomersPrevBtn) {
      crmCustomersPrevBtn.addEventListener('click', () => {
        if (crmState.loadingCustomers) return;
        const nextOffset = Math.max(0, (crmState.customersOffset || 0) - CRM_PAGE_SIZE);
        if (nextOffset === crmState.customersOffset) return;
        crmState.customersOffset = nextOffset;
        loadCrmCustomers();
      });
    }
    if (crmCustomersNextBtn) {
      crmCustomersNextBtn.addEventListener('click', () => {
        if (crmState.loadingCustomers) return;
        const total = crmState.customersTotal || 0;
        const nextOffset = (crmState.customersOffset || 0) + CRM_PAGE_SIZE;
        if (nextOffset >= total) return;
        crmState.customersOffset = nextOffset;
        loadCrmCustomers();
      });
    }
    if (crmProspectsRefreshBtn) {
      crmProspectsRefreshBtn.addEventListener('click', () => {
        if (crmState.prospectView === 'board') loadBoard();
        else loadCrmProspects();
      });
    }
    const dueFilterEl = document.getElementById('crmProspectDueFilter');
    if (dueFilterEl) {
      dueFilterEl.addEventListener('change', () => {
        crmState.prospectsDue = dueFilterEl.value || '';
        crmState.prospectsOffset = 0;
        loadCrmProspects();
      });
    }
    const viewToggleEl = document.getElementById('crmProspectViewToggle');
    if (viewToggleEl) {
      viewToggleEl.addEventListener('click', () => {
        setProspectView(crmState.prospectView === 'board' ? 'table' : 'board');
      });
    }
    const attentionChipEl = document.getElementById('crmProspectAttentionChip');
    if (attentionChipEl) {
      attentionChipEl.addEventListener('click', () => {
        crmState.prospectsAttention = !crmState.prospectsAttention;
        crmState.prospectsOffset = 0;
        // Attention is a table-view filter; switch out of the board to show it.
        if (crmState.prospectsAttention && crmState.prospectView === 'board') {
          setProspectView('table');
        } else {
          loadCrmProspects();
        }
      });
    }
    const pendingQuotesChipEl = document.getElementById('crmProspectPendingQuotesChip');
    if (pendingQuotesChipEl) {
      pendingQuotesChipEl.addEventListener('click', () => {
        crmState.prospectsPendingQuotes = !crmState.prospectsPendingQuotes;
        crmState.prospectsOffset = 0;
        // A quote-state worklist — show it in the table, not the claim board.
        if (crmState.prospectsPendingQuotes && crmState.prospectView === 'board') {
          setProspectView('table');
        } else {
          loadCrmProspects();
        }
      });
    }
    const targetsBtnEl = document.getElementById('crmProspectTargetsBtn');
    if (targetsBtnEl) {
      // Quick-link from the board toolbar → the full Targets nav sub-view.
      targetsBtnEl.addEventListener('click', () => {
        const nav = document.querySelector('.nav .nav-view[data-view="crm_targets"]');
        if (nav) nav.click();
      });
    }
    const targetsPeriodEl = document.getElementById('crmTargetsPeriod');
    if (targetsPeriodEl) {
      targetsPeriodEl.addEventListener('change', () => loadTargets(targetsPeriodEl.value || _currentPeriod()));
    }
    const targetsPrintBtn = document.getElementById('crmTargetsPrintBtn');
    if (targetsPrintBtn) {
      targetsPrintBtn.addEventListener('click', printTargets);
    }
    const targetsSaveBtn = document.getElementById('crmTargetsSaveBtn');
    if (targetsSaveBtn) {
      targetsSaveBtn.addEventListener('click', saveAllTargets);
    }
    const poolPrevBtn = document.getElementById('crmBoardPoolPrev');
    if (poolPrevBtn) {
      poolPrevBtn.addEventListener('click', () => {
        if (crmBoardPoolOffset <= 0) return;
        crmBoardPoolOffset = Math.max(0, crmBoardPoolOffset - BOARD_POOL_PAGE);
        loadBoard();
      });
    }
    const poolNextBtn = document.getElementById('crmBoardPoolNext');
    if (poolNextBtn) {
      poolNextBtn.addEventListener('click', () => {
        if (crmBoardPoolOffset + BOARD_POOL_PAGE >= crmBoardPoolTotal) return;
        crmBoardPoolOffset += BOARD_POOL_PAGE;
        loadBoard();
      });
    }
    const logContactForm = document.getElementById('crmLogContactForm');
    if (logContactForm) {
      logContactForm.addEventListener('submit', (e) => { e.preventDefault(); submitLogContact(); });
    }
    const dialogLogBtn = document.getElementById('crmProspectLogContactBtn');
    if (dialogLogBtn) {
      dialogLogBtn.addEventListener('click', () => {
        if (!crmEditingProspect) return;
        openLogContactDialog(crmEditingProspect, {
          afterSave: () => {
            refreshProspectsCurrentView();
            if (crmEditingProspect) loadProspectTimeline(crmEditingProspect.id);
          },
        });
      });
    }
    const dialogConfirmBtn = document.getElementById('crmProspectConfirmBtn');
    if (dialogConfirmBtn) {
      dialogConfirmBtn.addEventListener('click', () => {
        if (crmEditingProspect) confirmProspect(crmEditingProspect);
      });
    }
    const reclassifySaveBtn = document.getElementById('crmReclassifySave');
    if (reclassifySaveBtn) {
      reclassifySaveBtn.addEventListener('click', saveReclassify);
    }
    // Duplicate guard while creating a new prospect.
    if (crmProspectPhoneInput) crmProspectPhoneInput.addEventListener('input', scheduleDuplicateCheck);
    if (crmProspectEmailInput) crmProspectEmailInput.addEventListener('input', scheduleDuplicateCheck);
    if (crmProspectSearchInput) {
      crmProspectSearchInput.addEventListener('input', () => {
        if (crmProspectSearchTimer) clearTimeout(crmProspectSearchTimer);
        crmProspectSearchTimer = setTimeout(() => {
          crmState.prospectsSearch = crmProspectSearchInput.value.trim();
          crmState.prospectsOffset = 0;
          // A text search is meant to find a lead ANYWHERE, so it clears the
          // worklist filters that would otherwise silently narrow it — and shows
          // them cleared, rather than leaving a filter highlighted that is quietly
          // hiding matches. The owner filter was the real trap: it defaults to "My
          // prospects" for a rep, so searching for a colleague's lead found nothing.
          if (crmState.prospectsSearch) {
            crmState.prospectsStatus = '';
            updateProspectTabsActive();
            crmState.prospectsOwner = '';
            if (crmProspectOwnerFilter) crmProspectOwnerFilter.value = '';
            crmState.prospectsDue = '';
            const dueEl = document.getElementById('crmProspectDueFilter');
            if (dueEl) dueEl.value = '';
            crmState.prospectsAttention = false;
            const chipEl = document.getElementById('crmProspectAttentionChip');
            if (chipEl) chipEl.classList.remove('active');
            // Results are rendered into the TABLE. The section opens on the claim
            // board (Hot deals / My queue), which loadBoard() fills without ever
            // consulting the search — so typing there filtered the hidden table and
            // looked like a dead search box. Switch, as the attention and
            // pending-quotes chips already do. setProspectView loads for us.
            if (crmState.prospectView === 'board') { setProspectView('table'); return; }
          }
          loadCrmProspects();
        }, 250);
      });
    }
    if (crmProspectsPrevBtn) {
      crmProspectsPrevBtn.addEventListener('click', () => {
        if (crmState.loadingProspects) return;
        const nextOffset = Math.max(0, (crmState.prospectsOffset || 0) - CRM_PAGE_SIZE);
        if (nextOffset === crmState.prospectsOffset) return;
        crmState.prospectsOffset = nextOffset;
        loadCrmProspects();
      });
    }
    if (crmProspectsNextBtn) {
      crmProspectsNextBtn.addEventListener('click', () => {
        if (crmState.loadingProspects) return;
        const total = crmState.prospectsTotal || 0;
        const nextOffset = (crmState.prospectsOffset || 0) + CRM_PAGE_SIZE;
        if (nextOffset >= total) return;
        crmState.prospectsOffset = nextOffset;
        loadCrmProspects();
      });
    }
    if (crmProspectsNewBtn) {
      crmProspectsNewBtn.addEventListener('click', () => openCrmProspectDialog());
    }
    // Reconcile is root-only. Attach the handler unconditionally and let
    // visibility be (re)evaluated per load — mount() may run before the current
    // user/roles are known, so gating only here would hide it permanently.
    if (crmProspectsReconcileBtn) {
      crmProspectsReconcileBtn.addEventListener('click', () => openCrmReconcile());
      updateReconcileBtnVisibility();
    }
    if (crmReconcileForm) {
      crmReconcileForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!crmReconcileMatches.length) { closeCrmReconcileDialog(); return; }
        const ids = crmReconcileMatches.map((m) => m.prospect_id);
        if (crmReconcileConfirm) { crmReconcileConfirm.disabled = true; crmReconcileConfirm.textContent = 'Working…'; }
        try {
          const res = await adminAPI.fetch(API.crmProspectsReconcileApply(), { method: 'POST', body: JSON.stringify({ prospect_ids: ids }) });
          const skipped = res && res.skipped ? `, ${res.skipped} skipped` : '';
          showFlash(`Reconciled: ${(res && res.converted) || 0} prospect(s) marked converted${skipped}`);
          closeCrmReconcileDialog();
          await loadCrmProspects();
        } catch (err) {
          showFlash((err && err.message) || 'Failed to reconcile prospects', 'error');
        } finally {
          if (crmReconcileConfirm) { crmReconcileConfirm.disabled = false; crmReconcileConfirm.textContent = 'Mark as converted'; }
        }
      });
    }
    if (crmProspectTabs) {
      crmProspectTabs.addEventListener('click', (event) => {
        const btn = event.target.closest('button[data-status]');
        if (!btn) return;
        const nextStatus = btn.getAttribute('data-status') || '';
        if (nextStatus === (crmState.prospectsStatus || '')) return;
        crmState.prospectsStatus = nextStatus;
        crmState.prospectsOffset = 0;
        // Picking a status tab clears the search box: the endpoint applies the
        // status WITHIN the search results, so leaving a query in place would make
        // the tab look like it had filtered nothing.
        crmState.prospectsSearch = '';
        if (crmProspectSearchInput) crmProspectSearchInput.value = '';
        updateProspectTabsActive();
        loadCrmProspects();
      });
      updateProspectTabsActive();
    }
    if (crmProspectPipelineCard) {
      // Lazy-load the ownership chart the first time it's expanded.
      crmProspectPipelineCard.addEventListener('toggle', () => {
        if (crmProspectPipelineCard.open) loadProspectPipeline();
      });
    }
    if (crmProspectOwnerFilter) {
      // Supervisors/root get the full rep list so they can filter to one person.
      if (isProspectSupervisor()) loadProspectOwners();
      crmProspectOwnerFilter.value = crmState.prospectsOwner;
      crmProspectOwnerFilter.addEventListener('change', () => {
        crmState.prospectsOwner = crmProspectOwnerFilter.value;
        crmState.prospectsOffset = 0;
        loadCrmProspects();
      });
    }
    if (crmProspectForm) {
      crmProspectForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (crmProspectSubmitBtn) {
          crmProspectSubmitBtn.disabled = true;
          crmProspectSubmitBtn.textContent = 'Saving…';
        }
        const isEdit = !!crmEditingProspectId;
        const restoreLabel = isEdit ? 'Save changes' : 'Save prospect';
        const fullName = (crmProspectNameInput && crmProspectNameInput.value.trim()) || '';
        if (!fullName) {
          showFlash('Enter the prospect\'s name.', 'error');
          if (crmProspectSubmitBtn) {
            crmProspectSubmitBtn.disabled = false;
            crmProspectSubmitBtn.textContent = restoreLabel;
          }
          return;
        }
        const tags = (crmProspectTagsInput ? crmProspectTagsInput.value : '')
          .split(',').map((t) => t.trim()).filter(Boolean);
        const payload = {
          full_name: fullName,
          phone_number: crmProspectPhoneInput && crmProspectPhoneInput.value.trim() ? crmProspectPhoneInput.value.trim() : null,
          email: crmProspectEmailInput && crmProspectEmailInput.value.trim() ? crmProspectEmailInput.value.trim() : null,
          city: crmProspectCityInput && crmProspectCityInput.value.trim() ? crmProspectCityInput.value.trim() : null,
          street_address: crmProspectAddressInput && crmProspectAddressInput.value.trim() ? crmProspectAddressInput.value.trim() : null,
          company: crmProspectCompanyInput && crmProspectCompanyInput.value.trim() ? crmProspectCompanyInput.value.trim() : null,
          notes: crmProspectNotesInput && crmProspectNotesInput.value.trim() ? crmProspectNotesInput.value.trim() : null,
          tags,
          source: crmProspectSourceInput ? crmProspectSourceInput.value : 'unknown',
        };
        // Status is editable only for existing prospects; new ones start as "new".
        if (isEdit && crmProspectStatusInput) {
          payload.status = crmProspectStatusInput.value;
        }
        // Supervisor reassignment (owner field only shown to supervisors in edit).
        if (isEdit && crmProspectOwnerField && crmProspectOwnerField.style.display !== 'none' && crmProspectOwnerInput) {
          const ownerVal = crmProspectOwnerInput.value;
          payload.owner_user_id = ownerVal === '' ? null : Number(ownerVal);
        }
        try {
          if (isEdit) {
            await adminAPI.fetch(API.crmProspect(crmEditingProspectId), { method: 'PATCH', body: JSON.stringify(payload) });
            showFlash('Prospect updated');
          } else {
            await adminAPI.fetch(API.crmProspects(), { method: 'POST', body: JSON.stringify(payload) });
            showFlash('Prospect saved');
          }
          closeCrmProspectDialog();
          if (!isEdit) {
            crmState.prospectsSearch = '';
            crmState.prospectsOffset = 0;
            if (crmProspectSearchInput) crmProspectSearchInput.value = '';
          }
          await loadCrmProspects();
        } catch (err) {
          showFlash(err?.message || (isEdit ? 'Failed to update prospect' : 'Failed to save prospect'), 'error');
        } finally {
          if (crmProspectSubmitBtn) {
            crmProspectSubmitBtn.disabled = false;
            crmProspectSubmitBtn.textContent = restoreLabel;
          }
        }
      });
    }
    if (crmCustomerForm) {
      crmCustomerForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!crmEditingCustomer) return;
        const orig = crmEditingCustomer;
        const name = (crmCustomerNameInput && crmCustomerNameInput.value.trim()) || '';
        if (!name) {
          showFlash('Display name is required.', 'error');
          return;
        }
        // Diff against the original so we only send (and only Porta-push) actual
        // changes; a segment-only edit then reports porta_sync "skipped".
        const payload = {};
        if (name !== (orig.display_name || '')) payload.display_name = name;
        const seg = crmCustomerSegmentInput ? crmCustomerSegmentInput.value : '';
        if (seg !== (orig.segment || '')) payload.segment = seg || null;
        [['primary_phone', crmCustomerPhoneInput], ['primary_email', crmCustomerEmailInput],
          ['address', crmCustomerAddressInput], ['city', crmCustomerCityInput],
          ['region', crmCustomerRegionInput]].forEach(([key, el]) => {
          const v = el ? el.value.trim() : '';
          if (v !== (orig[key] || '')) payload[key] = v || null;
        });
        if (!Object.keys(payload).length) {
          showFlash('No changes to save.', 'info');
          closeCrmCustomerDialog();
          return;
        }
        if (crmCustomerSubmitBtn) { crmCustomerSubmitBtn.disabled = true; crmCustomerSubmitBtn.textContent = 'Saving…'; }
        try {
          const updated = await adminAPI.fetch(API.crmCustomer(orig.id), { method: 'PATCH', body: JSON.stringify(payload) });
          const sync = updated && updated.porta_sync;
          let msg = 'Customer updated';
          if (sync === 'synced') msg = 'Customer updated and synced to PortaOne';
          else if (sync === 'failed') msg = 'Customer saved, but PortaOne sync failed';
          else if (sync === 'no_account') msg = 'Customer updated (no PortaOne account)';
          showFlash(msg, sync === 'failed' ? 'error' : undefined);
          closeCrmCustomerDialog();
          await loadCrmCustomers();
        } catch (err) {
          showFlash((err && err.message) || 'Failed to update customer', 'error');
        } finally {
          if (crmCustomerSubmitBtn) { crmCustomerSubmitBtn.disabled = false; crmCustomerSubmitBtn.textContent = 'Save changes'; }
        }
      });
    }
    mounted = true;
  }

  // ---- Needs-attention chip --------------------------------------------
  async function updateAttentionChip() {
    const chip = document.getElementById('crmProspectAttentionChip');
    const pend = document.getElementById('crmProspectPendingQuotesChip');
    if (!chip && !pend) return;
    if (chip) chip.classList.toggle('primary', !!crmState.prospectsAttention);
    if (pend) pend.classList.toggle('primary', !!crmState.prospectsPendingQuotes);
    try {
      const data = await adminAPI.fetch(API.crmProspectsSummary(), { method: 'GET' });
      if (chip) {
        const n = Number(data && data.attention_count) || 0;
        chip.style.display = (n > 0 || crmState.prospectsAttention) ? '' : 'none';
        chip.textContent = n > 0 ? `⚠ Needs attention (${n})` : '⚠ Needs attention';
      }
      if (pend) {
        // Root-only approval worklist chip.
        const pn = Number(data && data.pending_quotes_count) || 0;
        const show = hasRole('root') && (pn > 0 || crmState.prospectsPendingQuotes);
        pend.style.display = show ? '' : 'none';
        pend.textContent = pn > 0 ? `📝 Pending quotes (${pn})` : '📝 Pending quotes';
      }
    } catch (err) {
      // Non-fatal.
    }
  }

  // ---- Targets & leaderboard -------------------------------------------
  function _currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  // Targets is now a full nav sub-view. `loadTargets()` is the registry entry
  // point; with no arg it reads the period picker (defaulting to this month).
  let crmTargetsRows = [];
  let crmTargetsPeriodLoaded = '';

  async function loadTargets(period) {
    const periodEl = document.getElementById('crmTargetsPeriod');
    if (periodEl && !periodEl.value) periodEl.value = _currentPeriod();
    const resolved = period || (periodEl && periodEl.value) || _currentPeriod();
    const tbody = document.getElementById('crmTargetsTbody');
    const hint = document.getElementById('crmTargetsHint');
    const chart = document.getElementById('crmTargetsChart');
    if (!tbody) return;
    crmTargetsPeriodLoaded = resolved;
    const canEdit = isProspectSupervisor();
    if (hint) hint.textContent = canEdit ? 'Editable — change a target, then click Save targets (or press Enter).' : 'Read-only (supervisors set targets)';
    const saveBtn = document.getElementById('crmTargetsSaveBtn');
    if (saveBtn) {
      saveBtn.style.display = canEdit ? '' : 'none';
      saveBtn.disabled = true;
      saveBtn.textContent = '💾 Save targets';
    }
    tbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>';
    if (chart) chart.innerHTML = '';
    try {
      const data = await adminAPI.fetch(API.crmProspectTargets(`period=${encodeURIComponent(resolved)}`), { method: 'GET' });
      const rows = Array.isArray(data && data.rows) ? data.rows : [];
      crmTargetsRows = rows;
      renderTargetsChart(chart, rows);
      tbody.innerHTML = '';
      if (!rows.length) {
        tbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No staff yet.</td></tr>';
        return;
      }
      rows.forEach((row, i) => {
        const tr = document.createElement('tr');
        const numTd = document.createElement('td');
        numTd.textContent = String(i + 1);
        const nameTd = document.createElement('td');
        nameTd.textContent = row.owner_name || `User ${row.owner_user_id}`;
        tr.appendChild(numTd);
        tr.appendChild(nameTd);
        TARGET_METRICS.forEach(({ key: metric, aKey, tKey }) => {
          const td = document.createElement('td');
          const actual = Number(row[aKey]) || 0;
          const target = Number(row[tKey]) || 0;
          const actualSpan = document.createElement('strong');
          actualSpan.textContent = String(actual);
          td.appendChild(actualSpan);
          td.appendChild(document.createTextNode(' / '));
          if (canEdit) {
            const input = document.createElement('input');
            input.type = 'number';
            input.min = '0';
            input.value = String(target);
            input.style.cssText = 'width:60px;';
            input.dataset.orig = String(target);
            input.dataset.owner = String(row.owner_user_id);
            input.dataset.metric = metric;
            input.title = 'Target — edit then click Save targets (or press Enter)';
            input.addEventListener('input', () => {
              const dirty = input.value !== input.dataset.orig;
              // Highlight unsaved edits so it's obvious a Save is pending.
              input.style.background = dirty ? '#fef9c3' : '';
              input.style.borderColor = dirty ? '#f59e0b' : '';
              updateTargetsSaveState();
            });
            input.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveAllTargets(); }
            });
            td.appendChild(input);
          } else {
            td.appendChild(document.createTextNode(String(target)));
          }
          // attainment colour
          if (target > 0) {
            actualSpan.style.color = actual >= target ? '#15803d' : (actual >= target * 0.5 ? '#E65100' : '#C62828');
          }
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      updateTargetsSaveState();
    } catch (err) {
      tbody.innerHTML = `<tr><td colspan="7" style="color:var(--err);">${(err && err.message) || 'Failed to load targets'}</td></tr>`;
    }
  }

  // All editable target inputs currently rendered, and the subset with unsaved
  // edits (value differs from the value last loaded from the server).
  function _targetInputs() {
    const tb = document.getElementById('crmTargetsTbody');
    return tb ? Array.from(tb.querySelectorAll('input[data-metric]')) : [];
  }
  function _dirtyTargetInputs() {
    return _targetInputs().filter((el) => el.value !== el.dataset.orig);
  }

  // Reflect unsaved-edit state in the Save button + hint so it's always clear
  // whether there is anything to save.
  function updateTargetsSaveState() {
    const btn = document.getElementById('crmTargetsSaveBtn');
    const hint = document.getElementById('crmTargetsHint');
    const n = _dirtyTargetInputs().length;
    if (btn) {
      btn.disabled = n === 0;
      btn.textContent = n ? `💾 Save ${n} change${n > 1 ? 's' : ''}` : '💾 Save targets';
    }
    if (hint && isProspectSupervisor()) {
      hint.textContent = n
        ? `${n} unsaved change${n > 1 ? 's' : ''} — click Save targets (or press Enter).`
        : 'Editable — change a target, then click Save targets (or press Enter).';
    }
  }

  // Commit every edited target in one click, then reload once.
  async function saveAllTargets() {
    const dirty = _dirtyTargetInputs();
    if (!dirty.length) return;
    const period = crmTargetsPeriodLoaded || _currentPeriod();
    const btn = document.getElementById('crmTargetsSaveBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
    dirty.forEach((el) => { el.disabled = true; });
    let saved = 0;
    let failed = 0;
    for (const el of dirty) {
      const target = Math.max(0, Math.floor(Number(el.value) || 0));
      try {
        await adminAPI.fetch(API.crmProspectTargets(), {
          method: 'PUT',
          body: JSON.stringify({
            owner_user_id: Number(el.dataset.owner),
            period,
            metric: el.dataset.metric,
            target_value: target,
          }),
        });
        saved += 1;
      } catch (err) {
        failed += 1;
      }
    }
    if (failed) showFlash(`${saved} saved, ${failed} failed`, 'error');
    else showFlash(`${saved} target${saved > 1 ? 's' : ''} saved`);
    // Reload once to refresh actuals/chart and reset the dirty/Save state.
    loadTargets(period);
  }

  // ---- Targets chart + print -------------------------------------------
  const TARGET_METRICS = [
    { key: 'conversions', label: 'Conversions', aKey: 'conversions_actual', tKey: 'conversions_target', color: '#2563eb' },
    { key: 'contacts', label: 'Contacts', aKey: 'contacts_actual', tKey: 'contacts_target', color: '#0891b2' },
    { key: 'qualified', label: 'Qualified', aKey: 'qualified_actual', tKey: 'qualified_target', color: '#7c3aed' },
    // Retention (customer care).
    { key: 'recoveries', label: 'Recoveries', aKey: 'recoveries_actual', tKey: 'recoveries_target', color: '#16a34a' },
    { key: 'retention_contacts', label: 'Win-back calls', aKey: 'retention_contacts_actual', tKey: 'retention_contacts_target', color: '#0d9488' },
  ];

  function _attainColor(actual, target) {
    if (!(target > 0)) return '#94a3b8';
    if (actual >= target) return '#15803d';
    if (actual >= target * 0.5) return '#E65100';
    return '#C62828';
  }

  // Hand-rolled flexbox bar chart — conversions attainment per rep (the
  // headline metric). No chart library; mirrors the pipeline bars elsewhere.
  function renderTargetsChart(container, rows) {
    if (!container) return;
    container.innerHTML = '';
    if (!Array.isArray(rows) || !rows.length) return;
    const heading = document.createElement('div');
    heading.style.cssText = 'font-size:12px; font-weight:600; color:var(--sub); margin-bottom:8px;';
    heading.textContent = 'Conversions vs target';
    container.appendChild(heading);

    const maxVal = Math.max(
      1,
      ...rows.map((r) => Math.max(Number(r.conversions_actual) || 0, Number(r.conversions_target) || 0)),
    );
    rows.forEach((row) => {
      const actual = Number(row.conversions_actual) || 0;
      const target = Number(row.conversions_target) || 0;
      const line = document.createElement('div');
      line.style.cssText = 'display:flex; align-items:center; gap:10px; margin-bottom:6px;';

      const name = document.createElement('div');
      name.style.cssText = 'flex:0 0 140px; font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;';
      name.textContent = row.owner_name || `User ${row.owner_user_id}`;
      line.appendChild(name);

      const track = document.createElement('div');
      track.style.cssText = 'position:relative; flex:1; height:18px; background:var(--panel-2,#f1f5f9); border-radius:4px; overflow:hidden;';
      const fill = document.createElement('div');
      const pct = Math.max(2, Math.round((actual / maxVal) * 100));
      fill.style.cssText = `height:100%; width:${pct}%; background:${_attainColor(actual, target)}; border-radius:4px;`;
      track.appendChild(fill);
      // Target marker line.
      if (target > 0) {
        const mark = document.createElement('div');
        const tpct = Math.min(100, Math.round((target / maxVal) * 100));
        mark.title = `Target: ${target}`;
        mark.style.cssText = `position:absolute; top:0; bottom:0; left:${tpct}%; width:2px; background:#111827;`;
        track.appendChild(mark);
      }
      line.appendChild(track);

      const val = document.createElement('div');
      val.style.cssText = 'flex:0 0 64px; font-size:12px; text-align:right; color:var(--sub);';
      val.textContent = target > 0 ? `${actual} / ${target}` : `${actual}`;
      line.appendChild(val);

      container.appendChild(line);
    });
  }

  // Open a clean, self-contained print window for the current leaderboard.
  function printTargets() {
    const rows = Array.isArray(crmTargetsRows) ? crmTargetsRows : [];
    const period = crmTargetsPeriodLoaded || _currentPeriod();
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
    ));
    const bodyRows = rows.map((row, i) => {
      const cells = TARGET_METRICS.map((m) => {
        const a = Number(row[m.aKey]) || 0;
        const t = Number(row[m.tKey]) || 0;
        const color = _attainColor(a, t);
        return `<td style="color:${color};font-weight:600;">${a}${t > 0 ? ` / ${t}` : ''}</td>`;
      }).join('');
      return `<tr><td>${i + 1}</td><td>${esc(row.owner_name || ('User ' + row.owner_user_id))}</td>${cells}</tr>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8" />
      <title>Sales targets — ${esc(period)}</title>
      <style>
        body{font-family:system-ui,Segoe UI,Roboto,sans-serif;margin:24px;color:#111827;}
        h1{font-size:18px;margin:0 0 4px;}
        .sub{color:#6b7280;font-size:12px;margin-bottom:16px;}
        table{border-collapse:collapse;width:100%;font-size:13px;}
        th,td{border:1px solid #d1d5db;padding:6px 10px;text-align:left;}
        th{background:#f3f4f6;}
        @media print{button{display:none;}}
      </style></head><body>
      <h1>CRM targets &amp; leaderboard</h1>
      <div class="sub">Period: ${esc(period)} • Actual / Target</div>
      <table><thead><tr><th>#</th><th>Staff</th>${TARGET_METRICS.map((m) => `<th>${esc(m.label)}</th>`).join('')}</tr></thead>
      <tbody>${bodyRows || `<tr><td colspan="${TARGET_METRICS.length + 2}">No staff yet.</td></tr>`}</tbody></table>
      <script>window.onload=function(){window.print();};<\/script>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { showFlash('Allow pop-ups to print targets', 'error'); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  // Refresh whichever prospects view is active (board or table).
  function refreshProspectsCurrentView() {
    if (crmState.prospectView === 'board') loadBoard();
    else loadCrmProspects();
  }

  // View entry point (called by the router when the Prospects tab opens):
  // honour the active view — board by default — instead of always loading the
  // table.
  function enterProspectsView() {
    mount();
    if (crmState.prospectView === 'board') setProspectView('board');
    else loadCrmProspects();
  }

  // View entry point for the Targets nav sub-view.
  function enterTargetsView() {
    mount();
    const periodEl = document.getElementById('crmTargetsPeriod');
    if (periodEl && !periodEl.value) periodEl.value = _currentPeriod();
    loadTargets(periodEl ? periodEl.value : _currentPeriod());
  }

  export default {
    mount,
    loadCustomers: loadCrmCustomers,
    loadProspects: enterProspectsView,
    loadTargets: enterTargetsView,
    onProspectChanged,
    openProspectDialog: openCrmProspectDialog,
    closeProspectDialog: closeCrmProspectDialog,
    formatLabel: formatCrmLabel,
    statusClass: crmStatusClass,
    truncate,
    getState: () => crmState,
    getCustomerDetailCache: () => crmCustomerDetailCache,
    getProspectDetailCache: () => crmProspectDetailCache,
  };
